// pages/SAPSourceViewer.jsx
// ABAP Source Viewer — menampilkan ISI source code satu program/include ABAP,
// dibaca LIVE dari SAP lewat ZRFC_READ_SOURCE (backend: sapSource.js).
//
// ⚠ BATAS YANG DISENGAJA — jangan dihapus dari UI:
//   1. Hanya NAMESPACE CUSTOM. Pagar ada di FM ABAP-nya: program SAP standard
//      ditolak dengan RC=4 dan sampai di sini sebagai state 'denied'. Itu fitur
//      keamanan, bukan gangguan — UI menjelaskan batasnya, tidak menyarankan
//      cara melewatinya.
//   2. Autocomplete BUKAN daftar program. Sarannya berasal dari cache
//      sap_source_line, yaitu program yang PERNAH terbaca. Nama yang tidak
//      muncul di saran belum tentu tidak ada — karena itu input tetap bebas
//      diketik, dan teks pembantunya menyebut hal ini secara eksplisit.
//   3. Baris > 255 karakter DIPOTONG OLEH SAP, bukan oleh halaman ini. Bila
//      LINE_TRUNC > 0, peringatannya harus muncul: kode yang terpotong diam-diam
//      adalah kode yang salah dibaca.
//   4. Mode Function Module BUKAN SE37 penuh: tidak ada Test/Execute (F8) dan
//      tidak ada edit. Keduanya menjalankan/mengubah kode di SAP, dan dashboard
//      ini read-only. Yang ada: Attributes, Import/Export/Changing/Tables,
//      Exceptions, dan Source code.
//   5. Source FM dibaca lewat PROGRAM UTAMA function group dengan WITH_INCLUDES,
//      bukan lewat include FM-nya langsung. Itu keputusan berbasis bukti — lihat
//      catatan panjang di transportOpen.js:593: include tunggal function group
//      mengembalikan nol baris tanpa RC. Karena itu satu permintaan bisa
//      membawa banyak include; blok milik FM yang diminta ditandai dan
//      ditampilkan lebih dulu, sisanya tertutup.
import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useAuth, api as axiosApi } from '../context/AuthContext';
import { useLang } from '../context/LanguageContext';
import { useActiveDashboard } from '../context/ActiveDashboardContext';
// Tab 'Custom Workbench': inventaris TADIR + penarikan source massal.
// Sengaja komponen terpisah, bukan halaman terpisah — pertanyaannya sama
// ("apa isi kode custom di sistem ini"), jadi tempatnya juga sama.
import SAPWorkbench from './SAPWorkbench';
import SAPCustomizing from './SAPCustomizing'; // tab Enhancements (27-10-2026)
// Kotak pencarian objek yang dipakai BERSAMA oleh semua tab. Sumbernya
// katalog Custom Workbench (sap_wb_object), bukan baca LIVE ke SAP.
import WbObjectSearch, { useWbSearch } from '../components/WbObjectSearch';

// ── useApi — pakai axios instance dari AuthContext (token via interceptor) ────
// Pola sama dengan SAPCustomizing.jsx; sengaja tidak diangkat ke shared module
// selama masih dua pemakai, karena abstraksi yang lahir dari dua contoh biasanya
// salah bentuk.
function useApi() {
  return useCallback(async (path, opts = {}) => {
    const method = (opts.method || 'GET').toLowerCase();
    const body   = opts.body ? JSON.parse(opts.body) : undefined;
    try {
      const res = (method === 'get' || method === 'delete')
        ? await axiosApi[method](path)
        : await axiosApi[method](path, body);
      return res.data;
    } catch (err) {
      throw new Error(err.response?.data?.error || err.message || 'API error');
    }
  }, []);
}

// Nama program yang sah — CERMIN dari PROG_RE di backend (sapSource.js).
// Divalidasi di klien semata agar tombolnya mati sebelum permintaan dikirim;
// gerbang yang mengikat tetap yang di backend.
const PROG_RE = /^[A-Z0-9_/=<>+-]{1,40}$/;

// Nama FM yang sah — CERMIN dari FM_RE di backend. Lebih ketat dari PROG_RE:
// nama FM tidak memuat '=' atau '<'.
const FM_RE = /^[A-Z0-9_/]{1,30}$/;

// Nama kelas — CERMIN dari CLS_RE di backend.
const CLS_RE = /^[A-Z0-9_/]{1,30}$/;

// Nama objek kamus — CERMIN dari DD_RE di backend. '.' tidak diizinkan: yang
// diminta pengguna tidak pernah berupa baris penanda '.INCLUDE'.
const DD_RE = /^[A-Z0-9_/]{1,30}$/;

const MONO = 'ui-monospace, SFMono-Regular, Menlo, Consolas, monospace';

// Tab parameter, urut seperti SE37. `key` cocok dengan kunci pada `params` dari
// backend. Tab yang kosong TETAP ditampilkan (seperti SE37) — tab yang hilang
// membuat orang bertanya-tanya apakah datanya tidak ada atau fiturnya tidak ada.
const PARAM_TABS = [
  { key: 'import',     tk: 'sapSrc.tab.import'     },
  { key: 'export',     tk: 'sapSrc.tab.export'     },
  { key: 'changing',   tk: 'sapSrc.tab.changing'   },
  { key: 'tables',     tk: 'sapSrc.tab.tables'     },
  { key: 'exceptions', tk: 'sapSrc.tab.exceptions' },
];

// Warna per state. Dipisah dari JSX supaya penambahan state baru tidak
// tersebar ke beberapa tempat.
const STATE_TONE = {
  code:    { bg: '#ecfdf5', border: '#a7f3d0', fg: '#065f46' },
  empty:   { bg: '#fffbeb', border: '#fde68a', fg: '#92400e' },
  missing: { bg: '#f1f5f9', border: '#cbd5e1', fg: '#475569' },
  denied:  { bg: '#fef2f2', border: '#fecaca', fg: '#991b1b' },
  auth:    { bg: '#fef2f2', border: '#fecaca', fg: '#991b1b' },
  error:   { bg: '#fef2f2', border: '#fecaca', fg: '#991b1b' },
};

// Pecah satu baris jadi potongan cocok / tidak cocok untuk penyorotan pencarian.
// Dikerjakan di sini, bukan dengan innerHTML + regex replace: menyuntikkan HTML
// dari isi source code adalah cara paling mudah membuat XSS dari kode ABAP.
function splitMatches(text, needle) {
  if (!needle) return [{ hit: false, s: text }];
  const out = [];
  const hay = text.toUpperCase(), nee = needle.toUpperCase();
  let i = 0;
  for (;;) {
    const at = hay.indexOf(nee, i);
    if (at < 0) { if (i < text.length) out.push({ hit: false, s: text.slice(i) }); break; }
    if (at > i) out.push({ hit: false, s: text.slice(i, at) });
    out.push({ hit: true, s: text.slice(at, at + needle.length) });
    i = at + needle.length;
  }
  return out.length ? out : [{ hit: false, s: text }];
}

// ── SourceGroups — renderer blok source, dipakai mode Program DAN mode FM ─────
// Diangkat jadi komponen bersama karena keduanya menampilkan hal yang sama
// persis. Dua salinan <pre> akan menyimpang begitu salah satunya diperbaiki.
//
// `first` = progname yang harus dibuka & ditaruh paling atas. Pada mode Program
// itu program yang diminta; pada mode FM itu include milik FM tersebut. Blok
// selain `first` DITUTUP secara default: satu function group bisa memuat puluhan
// include milik FM lain, dan menampilkan semuanya terbuka membuat orang membaca
// kode yang bukan dicarinya tanpa sadar.
function SourceGroups({ groups, first, find, t }) {
  const [closed, setClosed] = useState(null);   // null = belum disentuh pengguna

  const ordered = useMemo(() => {
    const rank = (n) => (first && n === first ? 0 : 1);
    return [...(groups || [])].sort((a, b) =>
      rank(a.progname) - rank(b.progname) || a.progname.localeCompare(b.progname));
  }, [groups, first]);

  // Keadaan awal dihitung, bukan disimpan: `first` bisa berubah saat pengguna
  // membuka FM lain, dan state yang menempel akan menutup blok yang seharusnya
  // terbuka.
  const isClosed = (name) => (closed && name in closed)
    ? closed[name]
    : !!(first && name !== first);

  return ordered.map(g => {
    const shut = isClosed(g.progname);
    const isFirst = !!first && g.progname === first;
    return (
      <div key={g.progname} style={{ marginBottom: 12, border: '1px solid #e2e8f0',
        borderRadius: 10, overflow: 'hidden' }}>
        <button
          onClick={() => setClosed(o => ({ ...(o || {}), [g.progname]: !shut }))}
          style={{ display: 'flex', width: '100%', justifyContent: 'space-between',
            alignItems: 'center', gap: 10, padding: '0.55rem 0.85rem', border: 'none',
            background: isFirst ? 'rgb(var(--brand-50))' : '#f8fafc', cursor: 'pointer', textAlign: 'left' }}
        >
          <span style={{ fontFamily: MONO, fontSize: 12.5, fontWeight: 700, color: '#0f172a' }}>
            {g.progname}
            {isFirst && (
              <span style={{ marginLeft: 8, padding: '1px 6px', borderRadius: 4, fontSize: 10.5,
                fontWeight: 700, background: 'rgb(var(--brand-100))', color: 'rgb(var(--brand-700))', fontFamily: 'inherit' }}>
                {t('sapSrc.requested')}
              </span>
            )}
          </span>
          <span style={{ fontSize: 11.5, color: '#64748b' }}>
            {g.lines.length} {t('sapSrc.linesUnit')} · {shut ? t('sapSrc.expand') : t('sapSrc.collapse')}
          </span>
        </button>

        {!shut && (
          <pre style={{ margin: 0, padding: '0.75rem 0.85rem', background: '#0f172a',
            color: '#e2e8f0', fontFamily: MONO, fontSize: 12.5, lineHeight: 1.55,
            maxHeight: 520, overflow: 'auto' }}>
            {g.lines.map(l => (
              <div key={l.lineno} style={{ whiteSpace: 'pre' }}>
                <span style={{ color: '#64748b', userSelect: 'none' }}>
                  {String(l.lineno).padStart(5, ' ')}
                </span>
                {'  '}
                {splitMatches(String(l.content || ''), (find || '').trim()).map((seg, i) =>
                  seg.hit
                    ? <mark key={i} style={{ background: '#fbbf24', color: '#0f172a' }}>{seg.s}</mark>
                    : <span key={i}>{seg.s}</span>
                )}
              </div>
            ))}
          </pre>
        )}
      </div>
    );
  });
}

// ── FMViewer — tampilan Function Module (mirip SE37) ─────────────────────────
function FMViewer({ api, connId, conn, t, initial, onJump }) {
  const [fm, setFm]       = useState('');
  const [sugOpen, setSugOpen] = useState(false);

  const [info, setInfo]   = useState(null);
  const [loading, setLoad]= useState(false);
  const [err, setErr]     = useState(null);

  const [tab, setTab]     = useState('import');
  const [raw, setRaw]     = useState(false);

  // Source dimuat MALAS: satu function group bisa ribuan baris, dan yang dicari
  // orang saat membuka sebuah FM biasanya signature-nya, bukan kodenya.
  const [src, setSrc]       = useState(null);
  const [srcLoad, setSrcLoad] = useState(false);
  const [srcErr, setSrcErr]   = useState(null);
  const [find, setFind]       = useState('');

  const fmOk = FM_RE.test(fm.trim().toUpperCase());
  const canLook = !!connId && fmOk && !loading;
  // Interface FM dibaca lewat path_readtable; hanya tab Source yang memerlukan
  // path_readsource. Membedakan keduanya berarti profil tanpa path_readsource
  // masih berguna di sini, bukan mati total.
  const srcAvail = !!(conn && conn.source_configured);

  // Pencarian objek dari KATALOG Custom Workbench (cache), bukan TFDIR LIVE.
  // Katalog TADIR tidak menyimpan function module satuan, jadi yang muncul di
  // sini adalah FUNCTION GROUP-nya — memilihnya membuka tab Custom Workbench.
  const wbSug = useWbSearch(api, connId, fm);

  const doLook = useCallback(async (override) => {
    const name = String(override != null ? override : fm).trim().toUpperCase();
    if (!connId || !FM_RE.test(name)) return;
    setLoad(true); setErr(null); setSugOpen(false);
    // Source dari FM SEBELUMNYA dibuang. Membiarkannya berarti tab Source
    // menampilkan kode function group lain di bawah signature yang baru.
    setSrc(null); setSrcErr(null); setTab('import');
    try {
      const d = await api(`/api/sap/source/fm?connectionId=${encodeURIComponent(connId)}&fm=${encodeURIComponent(name)}`);
      setInfo(d);
    } catch (e) { setErr(e.message); setInfo(null); }
    finally { setLoad(false); }
  }, [api, connId, fm]);

  // Dibuka dari tab lain lewat hasil pencarian lintas tipe.
  const initRef = useRef('');
  useEffect(() => {
    const n = String(initial || '').trim().toUpperCase();
    if (!n || initRef.current === n) return;
    initRef.current = n;
    setFm(n);
    doLook(n);
  }, [initial, doLook]);


  const loadSource = useCallback(async () => {
    if (!info || !info.source) return;
    setSrcLoad(true); setSrcErr(null);
    try {
      const d = await api(`/api/sap/source/read?connectionId=${encodeURIComponent(connId)}` +
                          `&program=${encodeURIComponent(info.source.program)}&withIncludes=1`);
      setSrc(d);
    } catch (e) { setSrcErr(e.message); setSrc(null); }
    finally { setSrcLoad(false); }
  }, [api, connId, info]);

  const a = (info && info.attrs) || null;
  const counts = (info && info.counts) || {};
  const otherKeys = Object.keys((info && info.params_other) || {});

  const rows = tab === 'source' ? [] : (
    (info && info.params && info.params[tab]) ||
    (info && info.params_other && info.params_other[tab.replace('other:', '')]) || []
  );

  return (
    <>
      {/* ── Pencarian FM ── */}
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10, alignItems: 'flex-start', marginBottom: 14 }}>
        <div style={{ position: 'relative', flex: '1 1 320px', minWidth: 260 }}>
          <input
            value={fm}
            onChange={e => { setFm(e.target.value.toUpperCase()); setSugOpen(true); }}
            onFocus={() => setSugOpen(true)}
            onBlur={() => setTimeout(() => setSugOpen(false), 150)}
            onKeyDown={e => { if (e.key === 'Enter' && canLook) doLook(); if (e.key === 'Escape') setSugOpen(false); }}
            placeholder={t('sapSrc.fmPlaceholder')}
            spellCheck={false}
            style={{ width: '100%', padding: '0.55rem 0.75rem', borderRadius: 8, fontSize: 13,
              fontFamily: MONO, letterSpacing: '0.02em',
              border: '1px solid ' + (fm && !fmOk ? '#fca5a5' : '#cbd5e1') }}
          />
          {fm && !fmOk && (
            <div style={{ fontSize: 11.5, color: '#b91c1c', marginTop: 4 }}>{t('sapSrc.fmInvalid')}</div>
          )}

            {sugOpen && (
              <WbObjectSearch
                rows={wbSug.rows}
                total={wbSug.total}
                err={wbSug.err}
                mode="fm"
                onPick={n => { setFm(n); setSugOpen(false); doLook(n); }}
                onJump={(m, n) => { setSugOpen(false); if (onJump) onJump(m, n); }}
              />
            )}
        </div>

        <button
          onClick={() => doLook()}
          disabled={!canLook}
          style={{ padding: '0.55rem 1.1rem', borderRadius: 8, border: 'none', fontSize: 13,
            fontWeight: 600, cursor: canLook ? 'pointer' : 'not-allowed',
            background: canLook ? 'rgb(var(--brand-600))' : '#94a3b8', color: '#fff' }}
        >
          {loading ? t('sapSrc.fmLooking') : t('sapSrc.fmLookup')}
        </button>
      </div>

      {err && (
        <div style={{ padding: '0.7rem 0.9rem', borderRadius: 8, marginBottom: 14,
          background: '#fef2f2', border: '1px solid #fecaca', color: '#991b1b', fontSize: 13 }}>
          {err}
        </div>
      )}

      {!info && !err && !loading && (
        <div style={{ padding: '1.25rem', borderRadius: 10, background: '#f8fafc',
          border: '1px dashed #cbd5e1', color: '#64748b', fontSize: 13 }}>
          {t('sapSrc.fmEmptyState')}
        </div>
      )}
      {!info && !err && !loading && connId && (
        <div style={{ marginTop: 12 }}>
          <SourceLanding api={api} connId={connId} kind="fm" onOpen={n => { setFm(n); doLook(n); }} />
        </div>
      )}

      {info && a && (
        <>
          {/* ── Atribut ── */}
          <div style={{ padding: '0.85rem 1rem', borderRadius: 10, marginBottom: 12,
            background: '#f8fafc', border: '1px solid #e2e8f0' }}>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, alignItems: 'baseline' }}>
              <span style={{ fontFamily: MONO, fontSize: 14, fontWeight: 700, color: '#0f172a' }}>
                {a.funcname}
              </span>
              {a.title && <span style={{ fontSize: 13, color: '#334155' }}>— {a.title}</span>}
              {a.remote && (
                <span style={{ padding: '1px 7px', borderRadius: 4, fontSize: 10.5, fontWeight: 700,
                  background: '#dcfce7', color: '#166534' }}>{t('sapSrc.attr.remote')}</span>
              )}
              {a.update_module && (
                <span style={{ padding: '1px 7px', borderRadius: 4, fontSize: 10.5, fontWeight: 700,
                  background: '#fef3c7', color: '#92400e' }}>{t('sapSrc.attr.updateModule')}</span>
              )}
              {!a.remote && !a.update_module && (
                <span style={{ padding: '1px 7px', borderRadius: 4, fontSize: 10.5, fontWeight: 700,
                  background: '#e2e8f0', color: '#475569' }}>{t('sapSrc.attr.normal')}</span>
              )}
            </div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px 22px', marginTop: 8, fontSize: 12 }}>
              <span style={{ color: '#64748b' }}>{t('sapSrc.attr.group')}:{' '}
                <span style={{ fontFamily: MONO, color: '#0f172a' }}>{a.area || '—'}</span></span>
              <span style={{ color: '#64748b' }}>{t('sapSrc.attr.mainProg')}:{' '}
                <span style={{ fontFamily: MONO, color: '#0f172a' }}>{a.pname || '—'}</span></span>
              <span style={{ color: '#64748b' }}>{t('sapSrc.attr.include')}:{' '}
                <span style={{ fontFamily: MONO, color: '#0f172a' }}>{a.include_name || '—'}</span></span>
              {typeof info.took_ms === 'number' && (
                <span style={{ color: '#94a3b8' }}>{info.took_ms} ms · {info.table_fm}</span>
              )}
            </div>
          </div>

          {/* Peringatan dari backend ditampilkan, tidak ditelan. */}
          {!!(info.warnings || []).length && (
            <div style={{ padding: '0.6rem 0.85rem', borderRadius: 8, marginBottom: 12,
              background: '#fffbeb', border: '1px solid #fde68a', color: '#92400e', fontSize: 12 }}>
              <b>{t('sapSrc.warnTitle')}</b>
              <ul style={{ margin: '4px 0 0', paddingLeft: 18 }}>
                {info.warnings.map((w, i) => <li key={i}>{w}</li>)}
              </ul>
            </div>
          )}

          {/* ── Tab ── */}
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, marginBottom: 10,
            borderBottom: '1px solid #e2e8f0', paddingBottom: 6 }}>
            {PARAM_TABS.map(x => (
              <button key={x.key} onClick={() => setTab(x.key)}
                style={{ padding: '0.35rem 0.7rem', borderRadius: 7, fontSize: 12.5,
                  fontWeight: tab === x.key ? 700 : 500, cursor: 'pointer',
                  border: '1px solid ' + (tab === x.key ? 'rgb(var(--brand-600))' : 'transparent'),
                  background: tab === x.key ? 'rgb(var(--brand-50))' : 'transparent',
                  color: tab === x.key ? 'rgb(var(--brand-700))' : '#475569' }}>
                {t(x.tk)} <span style={{ color: '#94a3b8' }}>{counts[x.key] || 0}</span>
              </button>
            ))}
            {otherKeys.map(k => (
              <button key={k} onClick={() => setTab('other:' + k)}
                style={{ padding: '0.35rem 0.7rem', borderRadius: 7, fontSize: 12.5,
                  fontWeight: tab === 'other:' + k ? 700 : 500, cursor: 'pointer',
                  border: '1px solid ' + (tab === 'other:' + k ? '#b45309' : 'transparent'),
                  background: tab === 'other:' + k ? '#fffbeb' : 'transparent', color: '#92400e' }}>
                {t('sapSrc.tab.other').replace('{code}', k)}{' '}
                <span style={{ color: '#b45309' }}>{info.params_other[k].length}</span>
              </button>
            ))}
            {/* Tab Source butuh path_readsource; tab lainnya hanya butuh
                path_readtable. Karena itu ia dimatikan sendiri, bukan
                menjatuhkan seluruh tampilan FM. */}
            {info.source && (
              <button
                onClick={() => { setTab('source'); if (!src && !srcLoad) loadSource(); }}
                disabled={!srcAvail}
                title={srcAvail ? '' : t('sapSrc.noPath')}
                style={{ padding: '0.35rem 0.7rem', borderRadius: 7, fontSize: 12.5,
                  fontWeight: tab === 'source' ? 700 : 500,
                  cursor: srcAvail ? 'pointer' : 'not-allowed',
                  border: '1px solid ' + (tab === 'source' ? 'rgb(var(--brand-600))' : 'transparent'),
                  background: tab === 'source' ? 'rgb(var(--brand-50))' : 'transparent',
                  color: !srcAvail ? '#cbd5e1' : (tab === 'source' ? 'rgb(var(--brand-700))' : '#475569') }}>
                {t('sapSrc.tab.source')}
              </button>
            )}
          </div>

          {/* ── Isi tab parameter ── */}
          {tab !== 'source' && (
            <>
              <label style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 12,
                color: '#64748b', marginBottom: 8 }}>
                <input type="checkbox" checked={raw} onChange={e => setRaw(e.target.checked)} />
                {t('sapSrc.rawCols')}
              </label>

              {rows.length === 0 ? (
                <div style={{ padding: '1rem', borderRadius: 8, background: '#f8fafc',
                  border: '1px solid #e2e8f0', color: '#64748b', fontSize: 13 }}>
                  {t('sapSrc.tabEmpty')}
                </div>
              ) : (
                <div style={{ overflowX: 'auto', border: '1px solid #e2e8f0', borderRadius: 10 }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12.5 }}>
                    <thead>
                      <tr style={{ background: '#f8fafc', textAlign: 'left' }}>
                        {raw && <th style={{ padding: '0.5rem 0.7rem', color: '#64748b', fontWeight: 600 }}>PPOSITION</th>}
                        <th style={{ padding: '0.5rem 0.7rem', color: '#64748b', fontWeight: 600 }}>{t('sapSrc.col.param')}</th>
                        <th style={{ padding: '0.5rem 0.7rem', color: '#64748b', fontWeight: 600 }}>{t('sapSrc.col.typeRef')}</th>
                        <th style={{ padding: '0.5rem 0.7rem', color: '#64748b', fontWeight: 600 }}>{t('sapSrc.col.default')}</th>
                        <th style={{ padding: '0.5rem 0.7rem', color: '#64748b', fontWeight: 600 }}>{t('sapSrc.col.optional')}</th>
                        <th style={{ padding: '0.5rem 0.7rem', color: '#64748b', fontWeight: 600 }}>{t('sapSrc.col.text')}</th>
                        {raw && <th style={{ padding: '0.5rem 0.7rem', color: '#64748b', fontWeight: 600 }}>REFERENCE</th>}
                        {raw && <th style={{ padding: '0.5rem 0.7rem', color: '#64748b', fontWeight: 600 }}>TYPE</th>}
                        {raw && <th style={{ padding: '0.5rem 0.7rem', color: '#64748b', fontWeight: 600 }}>REF_CLASS</th>}
                      </tr>
                    </thead>
                    <tbody>
                      {rows.map(r => (
                        <tr key={r.parameter} style={{ borderTop: '1px solid #f1f5f9' }}>
                          {raw && <td style={{ padding: '0.45rem 0.7rem', fontFamily: MONO, color: '#94a3b8' }}>{r.position || ''}</td>}
                          <td style={{ padding: '0.45rem 0.7rem', fontFamily: MONO, fontWeight: 600, color: '#0f172a' }}>{r.parameter}</td>
                          <td style={{ padding: '0.45rem 0.7rem', fontFamily: MONO, color: '#334155' }}>{r.type_ref || '—'}</td>
                          <td style={{ padding: '0.45rem 0.7rem', fontFamily: MONO, color: '#334155' }}>{r.default || '—'}</td>
                          <td style={{ padding: '0.45rem 0.7rem' }}>
                            <span style={{ padding: '1px 6px', borderRadius: 4, fontSize: 10.5, fontWeight: 700,
                              background: r.optional ? '#e2e8f0' : 'rgb(var(--brand-100))',
                              color: r.optional ? '#475569' : 'rgb(var(--brand-700))' }}>
                              {r.optional ? t('sapSrc.optYes') : t('sapSrc.optNo')}
                            </span>
                          </td>
                          <td style={{ padding: '0.45rem 0.7rem', color: '#475569' }}>{r.text || '—'}</td>
                          {raw && <td style={{ padding: '0.45rem 0.7rem', fontFamily: MONO, color: '#94a3b8' }}>{r.raw.REFERENCE || ''}</td>}
                          {raw && <td style={{ padding: '0.45rem 0.7rem', fontFamily: MONO, color: '#94a3b8' }}>{r.raw.TYPE || ''}</td>}
                          {raw && <td style={{ padding: '0.45rem 0.7rem', fontFamily: MONO, color: '#94a3b8' }}>{r.raw.REF_CLASS || ''}</td>}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}

              {/* Teks parameter tanpa padanan di FUPARAREF — dilaporkan, bukan
                  dibuang. Selisih yang tidak terlihat adalah parameter yang
                  hilang dari layar tanpa jejak. */}
              {!!(info.text_orphans || []).length && (
                <div style={{ marginTop: 10, padding: '0.6rem 0.85rem', borderRadius: 8,
                  background: '#f8fafc', border: '1px solid #e2e8f0', color: '#64748b', fontSize: 12 }}>
                  <b>{t('sapSrc.orphanTitle')}</b>
                  <ul style={{ margin: '4px 0 0', paddingLeft: 18 }}>
                    {info.text_orphans.map(o => (
                      <li key={o.parameter}>
                        <span style={{ fontFamily: MONO }}>{o.parameter}</span>
                        {o.text ? ` — ${o.text}` : ''}{o.kind ? ` (KIND=${o.kind})` : ''}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </>
          )}

          {/* ── Tab source ── */}
          {tab === 'source' && (
            <>
              <div style={{ padding: '0.6rem 0.85rem', borderRadius: 8, marginBottom: 10,
                background: 'rgb(var(--brand-50))', border: '1px solid #bfdbfe', color: 'rgb(var(--brand-700))', fontSize: 12 }}>
                {t('sapSrc.fmSourceNote')
                  .replace('{prog}', (info.source && info.source.program) || '')
                  .replace('{inc}', (info.source && info.source.highlight) || '—')}
              </div>

              {srcLoad && (
                <div style={{ padding: '1rem', borderRadius: 8, background: '#f8fafc',
                  border: '1px solid #e2e8f0', color: '#64748b', fontSize: 13 }}>
                  {t('sapSrc.reading')}
                </div>
              )}
              {srcErr && (
                <div style={{ padding: '0.7rem 0.9rem', borderRadius: 8, marginBottom: 10,
                  background: '#fef2f2', border: '1px solid #fecaca', color: '#991b1b', fontSize: 13 }}>
                  {srcErr}
                </div>
              )}
              {src && !srcLoad && (
                <>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10, alignItems: 'center', marginBottom: 10 }}>
                    <input
                      value={find}
                      onChange={e => setFind(e.target.value)}
                      placeholder={t('sapSrc.findPlaceholder')}
                      spellCheck={false}
                      style={{ flex: '1 1 220px', minWidth: 180, padding: '0.45rem 0.7rem', borderRadius: 8,
                        border: '1px solid #cbd5e1', fontSize: 12.5, fontFamily: MONO }}
                    />
                    <span style={{ fontSize: 12, color: '#94a3b8' }}>
                      {src.total_lines} {t('sapSrc.linesUnit')} · {(src.groups || []).length} {t('sapSrc.progUnit')}
                    </span>
                    <button onClick={loadSource}
                      style={{ padding: '0.45rem 0.85rem', borderRadius: 8, border: '1px solid #cbd5e1',
                        background: '#fff', color: '#334155', fontSize: 12.5, fontWeight: 600, cursor: 'pointer' }}>
                      {t('sapSrc.reload')}
                    </button>
                  </div>
                  {!!src.trunc && (
                    <div style={{ padding: '0.6rem 0.85rem', borderRadius: 8, marginBottom: 10,
                      background: '#fffbeb', border: '1px solid #fde68a', color: '#92400e', fontSize: 12.5 }}>
                      {t('sapSrc.truncWarn').replace('{n}', String(src.trunc))}
                    </div>
                  )}
                  <SourceGroups
                    groups={src.groups || []}
                    first={(info.source && info.source.highlight) || ''}
                    find={find} t={t} />
                </>
              )}
            </>
          )}
        </>
      )}
    </>
  );
}

// ── TablePeek — sampel isi tabel ─────────────────────────────────────────────
// KELAS BERBEDA dari sisa halaman ini: yang lain menampilkan metadata, panel ini
// menampilkan DATA BISNIS. Karena itu izinnya sendiri ('sap_table_peek') dan
// panelnya tidak pernah muncul tanpa izin itu — bukan muncul lalu gagal 403,
// yang hanya memberi tahu orang bahwa ada pintu yang tidak boleh dibuka.
//
// Anggaran 512 byte dihitung di klien dari LENGTH metadata RFC_READ_TABLE,
// sehingga tombolnya mati SEBELUM permintaan dikirim. Gerbang yang mengikat
// tetap yang di backend; yang di sini semata agar orang tidak menebak-nebak.
function TablePeek({ api, connId, table, t }) {
  const [meta, setMeta]   = useState(null);
  const [mErr, setMErr]   = useState(null);
  const [mLoad, setMLoad] = useState(false);

  const [sel, setSel]       = useState([]);          // [] = auto-fit
  const [fFilter, setFFilter] = useState('');
  const [rows, setRows]     = useState(30);

  const [res, setRes]     = useState(null);
  const [err, setErr]     = useState(null);
  const [load, setLoad]   = useState(false);

  // Tabel berganti → SEMUA state panel dibuang. Membiarkan pilihan field tabel
  // sebelumnya menempel akan menghasilkan permintaan berisi nama field yang
  // tidak ada di tabel baru, dan pesan errornya membingungkan.
  useEffect(() => {
    setMeta(null); setMErr(null); setSel([]); setFFilter('');
    setRes(null); setErr(null); setRows(30);
  }, [table, connId]);

  const loadMeta = useCallback(async () => {
    setMLoad(true); setMErr(null);
    try {
      const d = await api(`/api/sap/source/peek-fields?connectionId=${encodeURIComponent(connId)}` +
                          `&table=${encodeURIComponent(table)}`);
      setMeta(d);
    } catch (e) { setMErr(e.message); setMeta(null); }
    finally { setMLoad(false); }
  }, [api, connId, table]);

  const lenOf = useMemo(() => {
    const m = new Map();
    for (const f of (meta && meta.fields) || []) m.set(f.name, f.length);
    return m;
  }, [meta]);

  const width = useMemo(() => sel.reduce((a, f) => a + (lenOf.get(f) || 0), 0), [sel, lenOf]);
  const limit = (meta && meta.limit) || 512;
  const over  = width > limit;

  const shownFields = useMemo(() => {
    const q = fFilter.trim().toUpperCase();
    const all = (meta && meta.fields) || [];
    return q ? all.filter(f => f.name.includes(q)) : all;
  }, [meta, fFilter]);

  const doPeek = useCallback(async () => {
    setLoad(true); setErr(null);
    try {
      const qs = `connectionId=${encodeURIComponent(connId)}&table=${encodeURIComponent(table)}` +
                 `&rows=${encodeURIComponent(rows)}` +
                 (sel.length ? `&fields=${encodeURIComponent(sel.join(','))}` : '');
      setRes(await api(`/api/sap/source/peek?${qs}`));
    } catch (e) { setErr(e.message); setRes(null); }
    finally { setLoad(false); }
  }, [api, connId, table, rows, sel]);

  if (!meta && !mLoad && !mErr) {
    return (
      <div style={{ marginTop: 14 }}>
        <button onClick={loadMeta}
          style={{ padding: '0.5rem 1rem', borderRadius: 8, border: '1px solid #cbd5e1',
            background: '#fff', color: '#334155', fontSize: 12.5, fontWeight: 600, cursor: 'pointer' }}>
          {t('sapSrc.peek.open')}
        </button>
        <div style={{ fontSize: 11.5, color: '#94a3b8', marginTop: 6 }}>{t('sapSrc.peek.hint')}</div>
      </div>
    );
  }

  return (
    <div style={{ marginTop: 14, padding: '0.85rem 1rem', borderRadius: 10,
      background: '#fff', border: '1px solid #e2e8f0' }}>
      <div style={{ fontSize: 13, fontWeight: 700, color: '#0f172a', marginBottom: 6 }}>
        {t('sapSrc.peek.title')}
      </div>
      <div style={{ padding: '0.55rem 0.8rem', borderRadius: 8, marginBottom: 10,
        background: '#fff1f2', border: '1px solid #fecdd3', color: '#9f1239', fontSize: 12 }}>
        {t('sapSrc.peek.note')}
      </div>

      {mLoad && <div style={{ fontSize: 12.5, color: '#64748b' }}>{t('sapSrc.ddLooking')}</div>}
      {mErr && (
        <div style={{ padding: '0.6rem 0.85rem', borderRadius: 8,
          background: '#fef2f2', border: '1px solid #fecaca', color: '#991b1b', fontSize: 12.5 }}>
          {mErr}
        </div>
      )}

      {meta && (
        <>
          {/* Tabel lebar: seluruh field tidak mungkin ditarik sekaligus. Dinyatakan
              di depan, bukan dibiarkan ditemukan lewat tombol yang gagal. */}
          {!meta.fits_whole && (
            <div style={{ padding: '0.55rem 0.8rem', borderRadius: 8, marginBottom: 10,
              background: '#fffbeb', border: '1px solid #fde68a', color: '#92400e', fontSize: 12 }}>
              {t('sapSrc.peek.wide')
                .replace('{total}', String(meta.total_width))
                .replace('{limit}', String(meta.limit))
                .replace('{fm}', meta.table_fm)}
            </div>
          )}

          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10, alignItems: 'center', marginBottom: 8 }}>
            <input value={fFilter} onChange={e => setFFilter(e.target.value.toUpperCase())}
              placeholder={t('sapSrc.peek.fieldFilter')} spellCheck={false}
              style={{ flex: '1 1 180px', minWidth: 150, padding: '0.4rem 0.65rem', borderRadius: 8,
                border: '1px solid #cbd5e1', fontSize: 12, fontFamily: MONO }} />
            <span style={{ fontSize: 12, fontWeight: 600, color: over ? '#b91c1c' : '#475569' }}>
              {sel.length
                ? t('sapSrc.peek.budget').replace('{n}', String(sel.length))
                    .replace('{w}', String(width)).replace('{limit}', String(limit))
                : t('sapSrc.peek.autoFit')}
            </span>
            <button onClick={() => setSel([])}
              style={{ padding: '0.35rem 0.7rem', borderRadius: 7, border: '1px solid #cbd5e1',
                background: '#fff', color: '#475569', fontSize: 12, cursor: 'pointer' }}>
              {t('sapSrc.peek.clear')}
            </button>
          </div>

          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 5, maxHeight: 150, overflowY: 'auto',
            padding: 6, border: '1px solid #f1f5f9', borderRadius: 8, marginBottom: 10 }}>
            {shownFields.map(f => {
              const on = sel.includes(f.name);
              return (
                <button key={f.name}
                  onClick={() => setSel(p => on ? p.filter(x => x !== f.name) : [...p, f.name])}
                  style={{ padding: '2px 7px', borderRadius: 6, fontSize: 11.5, cursor: 'pointer',
                    fontFamily: MONO, border: '1px solid ' + (on ? 'rgb(var(--brand-600))' : '#e2e8f0'),
                    background: on ? 'rgb(var(--brand-50))' : '#fff', color: on ? 'rgb(var(--brand-700))' : '#475569' }}>
                  {f.name}<span style={{ color: '#94a3b8' }}> {f.length}</span>
                </button>
              );
            })}
          </div>

          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10, alignItems: 'center' }}>
            <label style={{ fontSize: 12, color: '#475569' }}>
              {t('sapSrc.peek.rows')}{' '}
              <input type="number" min={1} max={meta.rows_max || 200} value={rows}
                onChange={e => setRows(Math.max(1, Math.min(meta.rows_max || 200, Number(e.target.value) || 1)))}
                style={{ width: 70, padding: '0.3rem 0.5rem', borderRadius: 7,
                  border: '1px solid #cbd5e1', fontSize: 12, fontFamily: MONO }} />
            </label>
            <button onClick={doPeek} disabled={over || load}
              style={{ padding: '0.45rem 1rem', borderRadius: 8, border: 'none', fontSize: 12.5,
                fontWeight: 600, cursor: (over || load) ? 'not-allowed' : 'pointer',
                background: (over || load) ? '#94a3b8' : '#e11d48', color: '#fff' }}>
              {load ? t('sapSrc.peek.fetching') : t('sapSrc.peek.fetch')}
            </button>
            {over && (
              <span style={{ fontSize: 12, color: '#b91c1c' }}>
                {t('sapSrc.peek.overBudget').replace('{w}', String(width)).replace('{limit}', String(limit))}
              </span>
            )}
          </div>

          {err && (
            <div style={{ marginTop: 10, padding: '0.6rem 0.85rem', borderRadius: 8,
              background: '#fef2f2', border: '1px solid #fecaca', color: '#991b1b', fontSize: 12.5 }}>
              {err}
            </div>
          )}

          {res && (
            <>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px 18px', margin: '10px 0 8px', fontSize: 12 }}>
                <span style={{ color: '#475569', fontWeight: 600 }}>
                  {t('sapSrc.peek.got').replace('{n}', String(res.row_count))
                    .replace('{req}', String(res.rows_requested))}
                </span>
                <span style={{ color: '#94a3b8' }}>
                  {res.columns.length} {t('sapSrc.dd.fields')} · {res.width}/{res.limit} byte
                  {typeof res.took_ms === 'number' ? ` · ${res.took_ms} ms` : ''}
                </span>
                {/* Baris lebih sedikit dari yang diminta = tabelnya memang pendek,
                    BUKAN terpotong. Dinyatakan supaya tidak salah dibaca. */}
                {res.exhausted && <span style={{ color: '#64748b' }}>{t('sapSrc.peek.exhausted')}</span>}
              </div>

              {!!(res.warnings || []).length && (
                <div style={{ padding: '0.55rem 0.8rem', borderRadius: 8, marginBottom: 8,
                  background: '#fffbeb', border: '1px solid #fde68a', color: '#92400e', fontSize: 12 }}>
                  {res.warnings.map((w, i) => <div key={i}>{w}</div>)}
                </div>
              )}

              {res.row_count === 0 ? (
                <div style={{ padding: '0.8rem', borderRadius: 8, background: '#f8fafc',
                  border: '1px solid #e2e8f0', color: '#64748b', fontSize: 12.5 }}>
                  {t('sapSrc.peek.empty')}
                </div>
              ) : (
                <div style={{ overflow: 'auto', maxHeight: 420, border: '1px solid #e2e8f0', borderRadius: 8 }}>
                  <table style={{ borderCollapse: 'collapse', fontSize: 12, fontFamily: MONO }}>
                    <thead>
                      <tr style={{ background: '#f8fafc' }}>
                        <th style={{ padding: '0.35rem 0.6rem', color: '#cbd5e1', fontWeight: 600,
                          position: 'sticky', top: 0, background: '#f8fafc', textAlign: 'right' }}>#</th>
                        {res.columns.map(cx => (
                          <th key={cx.name} style={{ padding: '0.35rem 0.6rem', color: '#64748b',
                            fontWeight: 600, whiteSpace: 'nowrap', textAlign: 'left',
                            position: 'sticky', top: 0, background: '#f8fafc' }}>
                            {cx.name}<span style={{ color: '#cbd5e1' }}> {cx.length}</span>
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {res.rows.map((r, i) => (
                        <tr key={i} style={{ borderTop: '1px solid #f1f5f9' }}>
                          <td style={{ padding: '0.3rem 0.6rem', color: '#cbd5e1', textAlign: 'right' }}>{i + 1}</td>
                          {res.columns.map(cx => (
                            <td key={cx.name} style={{ padding: '0.3rem 0.6rem', color: '#334155',
                              whiteSpace: 'pre', maxWidth: 320, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                              {r[cx.name] === '' || r[cx.name] == null ? '·' : String(r[cx.name])}
                            </td>
                          ))}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </>
          )}
        </>
      )}
    </div>
  );
}

// ── ClassViewer — Class Builder (mirip SE24) ─────────────────────────────────
// ⚠ BATAS YANG DISENGAJA: tidak ada tombol "lompat ke source method ini".
// transportOpen.js:557 sudah membuktikan nomor include per method (…CM001)
// TIDAK dapat diturunkan dari nama method — pemetaannya hanya ada lewat
// CL_OO_CLASSNAME_SERVICE, yang tidak bisa dipanggil dari RFC_READ_TABLE.
// Menebaknya akan menampilkan METHOD YANG SALAH tanpa tanda apa pun.
// Yang disediakan: tombol yang MENGISI kotak pencarian source dengan
// "METHOD <nama>" — itu pencarian teks, dan dilabeli sebagai pencarian.
function ClassViewer({ api, connId, conn, t, initial, onJump }) {
  const [cls, setCls]     = useState('');
  const [sugOpen, setSugOpen] = useState(false);

  const [info, setInfo]   = useState(null);
  const [loading, setLoad]= useState(false);
  const [err, setErr]     = useState(null);
  const [tab, setTab]     = useState('methods');
  const [filter, setFilter] = useState('');

  // Parameter dimuat per method saat barisnya dibuka — bukan sekaligus.
  const [openMtd, setOpenMtd] = useState(null);
  const [prm, setPrm]         = useState({});   // method → hasil
  const [prmLoad, setPrmLoad] = useState(null);
  const [prmErr, setPrmErr]   = useState({});

  const [src, setSrc]         = useState(null);
  const [srcLoad, setSrcLoad] = useState(false);
  const [srcErr, setSrcErr]   = useState(null);
  const [find, setFind]       = useState('');

  const clsOk = CLS_RE.test(cls.trim().toUpperCase());
  const canLook = !!connId && clsOk && !loading;
  const srcAvail = !!(conn && conn.source_configured);

  const wbSug = useWbSearch(api, connId, cls);

  const doLook = useCallback(async (override) => {
    const n = String(override != null ? override : cls).trim().toUpperCase();
    if (!connId || !CLS_RE.test(n)) return;
    setLoad(true); setErr(null); setSugOpen(false); setFilter(''); setTab('methods');
    // Semua state turunan dibuang: parameter dan source kelas SEBELUMNYA yang
    // menempel akan tampil di bawah signature kelas yang baru.
    setOpenMtd(null); setPrm({}); setPrmErr({});
    setSrc(null); setSrcErr(null); setFind('');
    if (override != null) setCls(n);
    try { setInfo(await api(`/api/sap/source/class?connectionId=${encodeURIComponent(connId)}&cls=${encodeURIComponent(n)}`)); }
    catch (e) { setErr(e.message); setInfo(null); }
    finally { setLoad(false); }
  }, [api, connId, cls]);

  // Dibuka dari tab lain lewat hasil pencarian lintas tipe.
  const initRef = useRef('');
  useEffect(() => {
    const n = String(initial || '').trim().toUpperCase();
    if (!n || initRef.current === n) return;
    initRef.current = n;
    setCls(n);
    doLook(n);
  }, [initial, doLook]);


  const toggleMtd = useCallback(async (name) => {
    if (openMtd === name) { setOpenMtd(null); return; }
    setOpenMtd(name);
    if (prm[name] || prmLoad === name) return;
    setPrmLoad(name);
    try {
      const d = await api(`/api/sap/source/class-params?connectionId=${encodeURIComponent(connId)}` +
                          `&cls=${encodeURIComponent(info.cls)}&method=${encodeURIComponent(name)}`);
      setPrm(p => ({ ...p, [name]: d }));
    } catch (e) { setPrmErr(p => ({ ...p, [name]: e.message })); }
    finally { setPrmLoad(null); }
  }, [api, connId, info, openMtd, prm, prmLoad]);

  const loadSource = useCallback(async () => {
    if (!info || !info.source) return;
    setSrcLoad(true); setSrcErr(null);
    try {
      setSrc(await api(`/api/sap/source/read?connectionId=${encodeURIComponent(connId)}` +
                       `&program=${encodeURIComponent(info.source.program)}&withIncludes=1`));
    } catch (e) { setSrcErr(e.message); setSrc(null); }
    finally { setSrcLoad(false); }
  }, [api, connId, info]);

  const a = (info && info.attrs) || null;
  const counts = (info && info.counts) || {};
  const otherComp = Object.keys((info && info.components_other) || {});

  const rows = useMemo(() => {
    if (!info) return [];
    const q = filter.trim().toUpperCase();
    const base = tab === 'methods' ? info.methods
               : tab === 'attributes' ? info.attributes
               : tab === 'events' ? info.events : [];
    return q ? base.filter(x => x.name.includes(q) || (x.text || '').toUpperCase().includes(q)) : base;
  }, [info, tab, filter]);

  const chip = (label, tone) => (
    <span style={{ padding: '1px 6px', borderRadius: 4, fontSize: 10, fontWeight: 700,
      background: tone === 'pub' ? '#dcfce7' : tone === 'prot' ? '#fef3c7' : tone === 'priv' ? '#e2e8f0' : 'rgb(var(--brand-100))',
      color: tone === 'pub' ? '#166534' : tone === 'prot' ? '#92400e' : tone === 'priv' ? '#475569' : 'rgb(var(--brand-700))' }}>
      {label}
    </span>
  );
  const expTone = (l) => l === 'public' ? 'pub' : l === 'protected' ? 'prot' : 'priv';

  const paramTable = (list, title) => !list.length ? null : (
    <div key={title} style={{ marginTop: 8 }}>
      <div style={{ fontSize: 11, fontWeight: 700, color: '#64748b', marginBottom: 3 }}>{title}</div>
      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 11.5 }}>
        <tbody>
          {list.map(x => (
            <tr key={x.name} style={{ borderTop: '1px solid #f1f5f9' }}>
              <td style={{ padding: '0.3rem 0.5rem', fontFamily: MONO, fontWeight: 600, whiteSpace: 'nowrap' }}>{x.name}</td>
              <td style={{ padding: '0.3rem 0.5rem', fontFamily: MONO, color: '#334155', whiteSpace: 'nowrap' }}>{x.type_ref || '—'}</td>
              <td style={{ padding: '0.3rem 0.5rem', whiteSpace: 'nowrap' }}>
                {x.optional && chip(t('sapSrc.optYes'), 'priv')}
                {x.default ? <span style={{ marginLeft: 5, fontFamily: MONO, color: '#94a3b8' }}>= {x.default}</span> : null}
              </td>
              <td style={{ padding: '0.3rem 0.5rem', color: '#475569' }}>{x.text || '—'}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );

  return (
    <>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10, alignItems: 'flex-start', marginBottom: 14 }}>
        <div style={{ position: 'relative', flex: '1 1 320px', minWidth: 260 }}>
          <input value={cls}
            onChange={e => { setCls(e.target.value.toUpperCase()); setSugOpen(true); }}
            onFocus={() => setSugOpen(true)}
            onBlur={() => setTimeout(() => setSugOpen(false), 150)}
            onKeyDown={e => { if (e.key === 'Enter' && canLook) doLook(); if (e.key === 'Escape') setSugOpen(false); }}
            placeholder={t('sapSrc.clsPlaceholder')} spellCheck={false}
            style={{ width: '100%', padding: '0.55rem 0.75rem', borderRadius: 8, fontSize: 13,
              fontFamily: MONO, letterSpacing: '0.02em',
              border: '1px solid ' + (cls && !clsOk ? '#fca5a5' : '#cbd5e1') }} />
          {cls && !clsOk && <div style={{ fontSize: 11.5, color: '#b91c1c', marginTop: 4 }}>{t('sapSrc.clsInvalid')}</div>}
              {sugOpen && (
                <WbObjectSearch
                  rows={wbSug.rows}
                  total={wbSug.total}
                  err={wbSug.err}
                  mode="cls"
                  onPick={n => { setSugOpen(false); doLook(n); }}
                  onJump={(m, n) => { setSugOpen(false); if (onJump) onJump(m, n); }}
                />
              )}
        </div>
        <button onClick={() => doLook()} disabled={!canLook}
          style={{ padding: '0.55rem 1.1rem', borderRadius: 8, border: 'none', fontSize: 13,
            fontWeight: 600, cursor: canLook ? 'pointer' : 'not-allowed',
            background: canLook ? 'rgb(var(--brand-600))' : '#94a3b8', color: '#fff' }}>
          {loading ? t('sapSrc.clsLooking') : t('sapSrc.clsLookup')}
        </button>
      </div>

      {err && (
        <div style={{ padding: '0.7rem 0.9rem', borderRadius: 8, marginBottom: 14,
          background: '#fef2f2', border: '1px solid #fecaca', color: '#991b1b', fontSize: 13 }}>{err}</div>
      )}
      {!info && !err && !loading && (
        <div style={{ padding: '1.25rem', borderRadius: 10, background: '#f8fafc',
          border: '1px dashed #cbd5e1', color: '#64748b', fontSize: 13 }}>{t('sapSrc.clsEmptyState')}</div>
      )}
      {!info && !err && !loading && connId && (
        <div style={{ marginTop: 12 }}>
          <SourceLanding api={api} connId={connId} kind="cls" onOpen={n => doLook(n)} />
        </div>
      )}

      {info && a && (
        <>
          <div style={{ padding: '0.85rem 1rem', borderRadius: 10, marginBottom: 12,
            background: '#f8fafc', border: '1px solid #e2e8f0' }}>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, alignItems: 'baseline' }}>
              <span style={{ fontFamily: MONO, fontSize: 14, fontWeight: 700, color: '#0f172a' }}>{a.clsname}</span>
              {a.text && <span style={{ fontSize: 13, color: '#334155' }}>— {a.text}</span>}
              {chip(a.exposure_label || a.exposure, expTone(a.exposure_label))}
              {a.abstract && chip('ABSTRACT', 'prot')}
              {a.final && chip('FINAL', 'x')}
            </div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px 22px', marginTop: 8, fontSize: 12 }}>
              {/* Superclass dari SEOMETAREL, BUKAN dari SEOCLASSDF.REFCLSNAME —
                  probe membuktikan field itu kosong meski superclass ada. */}
              <span style={{ color: '#64748b' }}>{t('sapSrc.cls.super')}:{' '}
                {a && info.superclasses.length
                  ? info.superclasses.map(x => (
                      <button key={x.name} onClick={() => doLook(x.name)}
                        style={{ padding: 0, border: 'none', background: 'transparent', cursor: 'pointer',
                          fontFamily: MONO, fontSize: 'inherit', color: 'rgb(var(--brand-600))', textDecoration: 'underline' }}>
                        {x.name}
                      </button>))
                  : <span style={{ fontFamily: MONO, color: '#94a3b8' }}>—</span>}
              </span>
              <span style={{ color: '#64748b' }}>{t('sapSrc.cls.changed')}:{' '}
                <span style={{ fontFamily: MONO, color: '#0f172a' }}>
                  {a.changed_by || a.author || '—'}{a.changed_on ? ` · ${a.changed_on}` : ''}</span></span>
              {typeof info.took_ms === 'number' && (
                <span style={{ color: '#94a3b8' }}>{info.took_ms} ms · {info.table_fm}</span>
              )}
            </div>
            {!!info.interfaces.length && (
              <div style={{ marginTop: 6, fontSize: 12, color: '#64748b' }}>
                {t('sapSrc.cls.implements')}:{' '}
                {info.interfaces.map(x => (
                  <span key={x.name} style={{ marginRight: 10 }}>
                    <span style={{ fontFamily: MONO, color: '#0f172a' }}>{x.name}</span>
                    {x.text ? <span style={{ color: '#94a3b8' }}> — {x.text}</span> : null}
                  </span>
                ))}
              </div>
            )}
            {!!(info.relations_other || []).length && (
              <div style={{ marginTop: 4, fontSize: 12, color: '#92400e' }}>
                {t('sapSrc.cls.relOther')}:{' '}
                {info.relations_other.map(x => (
                  <span key={x.name} style={{ fontFamily: MONO, marginRight: 10 }}>
                    {x.name} (RELTYPE={x.reltype})
                  </span>
                ))}
              </div>
            )}
          </div>

          {!!(info.warnings || []).length && (
            <div style={{ padding: '0.6rem 0.85rem', borderRadius: 8, marginBottom: 12,
              background: '#fffbeb', border: '1px solid #fde68a', color: '#92400e', fontSize: 12 }}>
              <b>{t('sapSrc.warnTitle')}</b>
              <ul style={{ margin: '4px 0 0', paddingLeft: 18 }}>
                {info.warnings.map((w, i) => <li key={i}>{w}</li>)}
              </ul>
            </div>
          )}

          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, marginBottom: 10,
            borderBottom: '1px solid #e2e8f0', paddingBottom: 6 }}>
            {[['methods','sapSrc.cls.tab.methods'],['attributes','sapSrc.cls.tab.attributes'],
              ['events','sapSrc.cls.tab.events']].map(([k, tk]) => (
              <button key={k} onClick={() => setTab(k)}
                style={{ padding: '0.35rem 0.7rem', borderRadius: 7, fontSize: 12.5,
                  fontWeight: tab === k ? 700 : 500, cursor: 'pointer',
                  border: '1px solid ' + (tab === k ? 'rgb(var(--brand-600))' : 'transparent'),
                  background: tab === k ? 'rgb(var(--brand-50))' : 'transparent',
                  color: tab === k ? 'rgb(var(--brand-700))' : '#475569' }}>
                {t(tk)} <span style={{ color: '#94a3b8' }}>{counts[k] || 0}</span>
              </button>
            ))}
            {otherComp.map(k => (
              <button key={k} onClick={() => setTab('other:' + k)}
                style={{ padding: '0.35rem 0.7rem', borderRadius: 7, fontSize: 12.5,
                  fontWeight: tab === 'other:' + k ? 700 : 500, cursor: 'pointer',
                  border: '1px solid ' + (tab === 'other:' + k ? '#b45309' : 'transparent'),
                  background: tab === 'other:' + k ? '#fffbeb' : 'transparent', color: '#92400e' }}>
                {t('sapSrc.cls.tab.other').replace('{code}', k)}{' '}
                <span style={{ color: '#b45309' }}>{info.components_other[k].length}</span>
              </button>
            ))}
            {info.source && (
              <button onClick={() => { setTab('source'); if (!src && !srcLoad) loadSource(); }}
                disabled={!srcAvail} title={srcAvail ? '' : t('sapSrc.noPath')}
                style={{ padding: '0.35rem 0.7rem', borderRadius: 7, fontSize: 12.5,
                  fontWeight: tab === 'source' ? 700 : 500,
                  cursor: srcAvail ? 'pointer' : 'not-allowed',
                  border: '1px solid ' + (tab === 'source' ? 'rgb(var(--brand-600))' : 'transparent'),
                  background: tab === 'source' ? 'rgb(var(--brand-50))' : 'transparent',
                  color: !srcAvail ? '#cbd5e1' : (tab === 'source' ? 'rgb(var(--brand-700))' : '#475569') }}>
                {t('sapSrc.tab.source')}
              </button>
            )}
          </div>

          {/* Kelompok CMPTYPE yang belum dikenal: NAMA saja, dan dinyatakan begitu. */}
          {tab.startsWith('other:') && (
            <>
              <div style={{ padding: '0.6rem 0.85rem', borderRadius: 8, marginBottom: 10,
                background: '#fffbeb', border: '1px solid #fde68a', color: '#92400e', fontSize: 12 }}>
                {t('sapSrc.cls.otherNote').replace('{code}', tab.slice(6))}
              </div>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 5 }}>
                {info.components_other[tab.slice(6)].map(n => (
                  <span key={n} style={{ padding: '2px 7px', borderRadius: 6, fontSize: 11.5,
                    fontFamily: MONO, background: '#f8fafc', border: '1px solid #e2e8f0', color: '#475569' }}>{n}</span>
                ))}
              </div>
            </>
          )}

          {(tab === 'methods' || tab === 'attributes' || tab === 'events') && (
            <>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10, alignItems: 'center', marginBottom: 8 }}>
                <input value={filter} onChange={e => setFilter(e.target.value.toUpperCase())}
                  placeholder={t('sapSrc.cls.filter')} spellCheck={false}
                  style={{ flex: '1 1 200px', minWidth: 160, padding: '0.4rem 0.65rem', borderRadius: 8,
                    border: '1px solid #cbd5e1', fontSize: 12, fontFamily: MONO }} />
                <span style={{ fontSize: 12, color: '#94a3b8' }}>{rows.length} / {counts[tab] || 0}</span>
              </div>

              {rows.length === 0 ? (
                <div style={{ padding: '1rem', borderRadius: 8, background: '#f8fafc',
                  border: '1px solid #e2e8f0', color: '#64748b', fontSize: 13 }}>{t('sapSrc.tabEmpty')}</div>
              ) : (
                <div style={{ border: '1px solid #e2e8f0', borderRadius: 10, overflow: 'hidden' }}>
                  {rows.map(x => (
                    <div key={x.name} style={{ borderTop: '1px solid #f1f5f9' }}>
                      <button
                        onClick={() => tab === 'methods' ? toggleMtd(x.name) : undefined}
                        style={{ display: 'flex', width: '100%', gap: 8, alignItems: 'center',
                          padding: '0.45rem 0.7rem', border: 'none', textAlign: 'left',
                          background: openMtd === x.name ? 'rgb(var(--brand-50))' : '#fff',
                          cursor: tab === 'methods' ? 'pointer' : 'default' }}>
                        <span style={{ fontFamily: MONO, fontSize: 12.5, fontWeight: 600, color: '#0f172a',
                          minWidth: 200 }}>{x.name}</span>
                        {chip(x.exposure_label || x.exposure, expTone(x.exposure_label))}
                        {x.decl_label && chip(x.decl_label, 'x')}
                        {x.abstract && chip('abstract', 'prot')}
                        {x.final && chip('final', 'priv')}
                        {x.readonly && chip('read-only', 'priv')}
                        {x.from_interface && (
                          <span style={{ fontFamily: MONO, fontSize: 10.5, color: '#7c3aed' }}>← {x.from_interface}</span>
                        )}
                        {x.type_ref && <span style={{ fontFamily: MONO, fontSize: 11.5, color: '#334155' }}>{x.type_ref}</span>}
                        {x.value ? <span style={{ fontFamily: MONO, fontSize: 11, color: '#94a3b8' }}>= {x.value}</span> : null}
                        <span style={{ flex: 1, fontSize: 12, color: '#475569' }}>{x.text || ''}</span>
                      </button>

                      {tab === 'methods' && openMtd === x.name && (
                        <div style={{ padding: '0.5rem 0.9rem 0.8rem', background: '#fafbfc' }}>
                          {prmLoad === x.name && <div style={{ fontSize: 12, color: '#64748b' }}>{t('sapSrc.reading')}</div>}
                          {prmErr[x.name] && <div style={{ fontSize: 12, color: '#b91c1c' }}>{prmErr[x.name]}</div>}
                          {prm[x.name] && (() => {
                            const d = prm[x.name];
                            const blocks = [
                              paramTable(d.params.importing, t('sapSrc.cls.importing')),
                              paramTable(d.params.exporting, t('sapSrc.cls.exporting')),
                              paramTable(d.params.changing,  t('sapSrc.cls.changing')),
                              paramTable(d.params.returning, t('sapSrc.cls.returning')),
                              ...Object.entries(d.params_other || {}).map(([code, list]) =>
                                paramTable(list, t('sapSrc.cls.paramOther').replace('{code}', code))),
                              d.exceptions.length ? paramTable(d.exceptions, t('sapSrc.tab.exceptions')) : null,
                            ].filter(Boolean);
                            return blocks.length ? blocks : (
                              <div style={{ fontSize: 12, color: '#94a3b8' }}>{t('sapSrc.cls.noParams')}</div>
                            );
                          })()}
                          {srcAvail && (
                            <button
                              onClick={() => { setFind('METHOD ' + x.name); setTab('source'); if (!src && !srcLoad) loadSource(); }}
                              style={{ marginTop: 8, padding: '0.3rem 0.65rem', borderRadius: 7,
                                border: '1px solid #cbd5e1', background: '#fff', color: '#334155',
                                fontSize: 11.5, cursor: 'pointer' }}>
                              {t('sapSrc.cls.findInSource')}
                            </button>
                          )}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </>
          )}

          {tab === 'source' && (
            <>
              <div style={{ padding: '0.6rem 0.85rem', borderRadius: 8, marginBottom: 10,
                background: 'rgb(var(--brand-50))', border: '1px solid #bfdbfe', color: 'rgb(var(--brand-700))', fontSize: 12 }}>
                {t('sapSrc.cls.sourceNote').replace('{pool}', (info.source && info.source.program) || '')}
              </div>
              {srcLoad && (
                <div style={{ padding: '1rem', borderRadius: 8, background: '#f8fafc',
                  border: '1px solid #e2e8f0', color: '#64748b', fontSize: 13 }}>{t('sapSrc.reading')}</div>
              )}
              {srcErr && (
                <div style={{ padding: '0.7rem 0.9rem', borderRadius: 8, marginBottom: 10,
                  background: '#fef2f2', border: '1px solid #fecaca', color: '#991b1b', fontSize: 13 }}>{srcErr}</div>
              )}
              {src && !srcLoad && (
                <>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10, alignItems: 'center', marginBottom: 10 }}>
                    <input value={find} onChange={e => setFind(e.target.value)}
                      placeholder={t('sapSrc.findPlaceholder')} spellCheck={false}
                      style={{ flex: '1 1 220px', minWidth: 180, padding: '0.45rem 0.7rem', borderRadius: 8,
                        border: '1px solid #cbd5e1', fontSize: 12.5, fontFamily: MONO }} />
                    <span style={{ fontSize: 12, color: '#94a3b8' }}>
                      {src.total_lines} {t('sapSrc.linesUnit')} · {(src.groups || []).length} {t('sapSrc.progUnit')}
                    </span>
                    <button onClick={loadSource}
                      style={{ padding: '0.45rem 0.85rem', borderRadius: 8, border: '1px solid #cbd5e1',
                        background: '#fff', color: '#334155', fontSize: 12.5, fontWeight: 600, cursor: 'pointer' }}>
                      {t('sapSrc.reload')}
                    </button>
                  </div>
                  {!!src.trunc && (
                    <div style={{ padding: '0.6rem 0.85rem', borderRadius: 8, marginBottom: 10,
                      background: '#fffbeb', border: '1px solid #fde68a', color: '#92400e', fontSize: 12.5 }}>
                      {t('sapSrc.truncWarn').replace('{n}', String(src.trunc))}
                    </div>
                  )}
                  <SourceGroups groups={src.groups || []} first={src.program} find={find} t={t} />
                </>
              )}
            </>
          )}
        </>
      )}
    </>
  );
}

// ── DDViewer — ABAP Dictionary (mirip SE11) ──────────────────────────────────
// Lingkup: TABEL & STRUKTUR, semua namespace termasuk SAP standard. Yang
// ditampilkan METADATA STRUKTUR — nama field, tipe, panjang, data element.
// ISI DATA tidak pernah dibaca di sini; itu wilayah halaman Table Maintenance
// dengan permission-nya sendiri.
function DDViewer({ api, connId, t, initial, onJump }) {
  const { user } = useAuth();
  // Panel sampel isi tabel TIDAK pernah dirender tanpa izin — bukan dirender
  // lalu gagal 403. Tombol yang hanya bisa memberi tahu bahwa ada pintu
  // terlarang bukan informasi yang berguna.
  const canPeek = !!user && (user.role === 'admin' ||
    (user.permissions || []).includes('sap_table_peek'));

  const [name, setName]   = useState('');
  const [sugOpen, setSugOpen] = useState(false);

  const [info, setInfo]   = useState(null);
  const [loading, setLoad]= useState(false);
  const [err, setErr]     = useState(null);
  const [filter, setFilter] = useState('');

  const nameOk = DD_RE.test(name.trim().toUpperCase());
  const canLook = !!connId && nameOk && !loading;

  const wbSug = useWbSearch(api, connId, name);

  // Satu fungsi untuk pencarian manual DAN navigasi (klik check table / struktur
  // include). Menyalinnya jadi dua akan membuat salah satu lupa mengosongkan
  // filter, dan hasilnya tabel baru yang tampak hanya punya beberapa field.
  const doLook = useCallback(async (override) => {
    const n = String(override != null ? override : name).trim().toUpperCase();
    if (!connId || !DD_RE.test(n)) return;
    setLoad(true); setErr(null); setSugOpen(false); setFilter('');
    if (override != null) setName(n);
    try {
      const d = await api(`/api/sap/source/dd?connectionId=${encodeURIComponent(connId)}&table=${encodeURIComponent(n)}`);
      setInfo(d);
    } catch (e) { setErr(e.message); setInfo(null); }
    finally { setLoad(false); }
  }, [api, connId, name]);

  // Dibuka dari tab lain lewat hasil pencarian lintas tipe.
  const initRef = useRef('');
  useEffect(() => {
    const n = String(initial || '').trim().toUpperCase();
    if (!n || initRef.current === n) return;
    initRef.current = n;
    setName(n);
    doLook(n);
  }, [initial, doLook]);


  const a = (info && info.attrs) || null;
  const fields = (info && info.fields) || [];

  // Filter menyaring baris FIELD saja; baris include/append selalu ikut tampil.
  // Menyembunyikannya saat menyaring akan membuat urutan field terlihat punya
  // lubang tanpa penjelasan.
  const shown = useMemo(() => {
    const q = filter.trim().toUpperCase();
    if (!q) return fields;
    return fields.filter(f => f.kind !== 'field'
      || f.fieldname.includes(q)
      || (f.rollname || '').includes(q)
      || (f.text || '').toUpperCase().includes(q));
  }, [fields, filter]);

  const navBtn = (label, target) => (
    <button onClick={() => doLook(target)} title={t('sapSrc.dd.navHint').replace('{name}', target)}
      style={{ padding: 0, border: 'none', background: 'transparent', cursor: 'pointer',
        fontFamily: MONO, fontSize: 'inherit', color: 'rgb(var(--brand-600))', textDecoration: 'underline' }}>
      {label}
    </button>
  );

  return (
    <>
      <div style={{ padding: '0.6rem 0.85rem', borderRadius: 8, marginBottom: 14,
        background: 'rgb(var(--brand-50))', border: '1px solid #bfdbfe', color: 'rgb(var(--brand-700))', fontSize: 12.5 }}>
        {t('sapSrc.dd.scopeNote')}
      </div>

      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10, alignItems: 'flex-start', marginBottom: 14 }}>
        <div style={{ position: 'relative', flex: '1 1 320px', minWidth: 260 }}>
          <input
            value={name}
            onChange={e => { setName(e.target.value.toUpperCase()); setSugOpen(true); }}
            onFocus={() => setSugOpen(true)}
            onBlur={() => setTimeout(() => setSugOpen(false), 150)}
            onKeyDown={e => { if (e.key === 'Enter' && canLook) doLook(); if (e.key === 'Escape') setSugOpen(false); }}
            placeholder={t('sapSrc.ddPlaceholder')}
            spellCheck={false}
            style={{ width: '100%', padding: '0.55rem 0.75rem', borderRadius: 8, fontSize: 13,
              fontFamily: MONO, letterSpacing: '0.02em',
              border: '1px solid ' + (name && !nameOk ? '#fca5a5' : '#cbd5e1') }}
          />
          {name && !nameOk && (
            <div style={{ fontSize: 11.5, color: '#b91c1c', marginTop: 4 }}>{t('sapSrc.ddInvalid')}</div>
          )}
              {sugOpen && (
                <WbObjectSearch
                  rows={wbSug.rows}
                  total={wbSug.total}
                  err={wbSug.err}
                  mode="dd"
                  onPick={n => { setSugOpen(false); doLook(n); }}
                  onJump={(m, n) => { setSugOpen(false); if (onJump) onJump(m, n); }}
                />
              )}
        </div>
        <button onClick={() => doLook()} disabled={!canLook}
          style={{ padding: '0.55rem 1.1rem', borderRadius: 8, border: 'none', fontSize: 13,
            fontWeight: 600, cursor: canLook ? 'pointer' : 'not-allowed',
            background: canLook ? 'rgb(var(--brand-600))' : '#94a3b8', color: '#fff' }}>
          {loading ? t('sapSrc.ddLooking') : t('sapSrc.ddLookup')}
        </button>
      </div>

      {err && (
        <div style={{ padding: '0.7rem 0.9rem', borderRadius: 8, marginBottom: 14,
          background: '#fef2f2', border: '1px solid #fecaca', color: '#991b1b', fontSize: 13 }}>
          {err}
        </div>
      )}

      {!info && !err && !loading && (
        <div style={{ padding: '1.25rem', borderRadius: 10, background: '#f8fafc',
          border: '1px dashed #cbd5e1', color: '#64748b', fontSize: 13 }}>
          {t('sapSrc.ddEmptyState')}
        </div>
      )}
      {!info && !err && !loading && connId && (
        <div style={{ marginTop: 12 }}>
          <SourceLanding api={api} connId={connId} kind="dd" onOpen={n => doLook(n)} />
        </div>
      )}

      {info && a && (
        <>
          <div style={{ padding: '0.85rem 1rem', borderRadius: 10, marginBottom: 12,
            background: '#f8fafc', border: '1px solid #e2e8f0' }}>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, alignItems: 'baseline' }}>
              <span style={{ fontFamily: MONO, fontSize: 14, fontWeight: 700, color: '#0f172a' }}>{a.tabname}</span>
              {a.text && <span style={{ fontSize: 13, color: '#334155' }}>— {a.text}</span>}
              <span style={{ padding: '1px 7px', borderRadius: 4, fontSize: 10.5, fontWeight: 700,
                background: '#e0e7ff', color: '#3730a3' }}>{a.tabclass_label}</span>
              <span style={{ padding: '1px 7px', borderRadius: 4, fontSize: 10.5, fontWeight: 700,
                background: a.maintain ? '#dcfce7' : '#e2e8f0',
                color: a.maintain ? '#166534' : '#475569' }}>
                {a.maintain ? t('sapSrc.dd.maintain') : t('sapSrc.dd.noMaintain')}
              </span>
            </div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px 22px', marginTop: 8, fontSize: 12 }}>
              {a.contflag && (
                <span style={{ color: '#64748b' }}>{t('sapSrc.dd.delivery')}:{' '}
                  <span style={{ color: '#0f172a' }}>{a.contflag_label || a.contflag}</span></span>
              )}
              <span style={{ color: '#64748b' }}>{t('sapSrc.dd.changed')}:{' '}
                <span style={{ color: '#0f172a', fontFamily: MONO }}>
                  {a.changed_by || '—'}{a.changed_on ? ` · ${a.changed_on}` : ''}</span></span>
              <span style={{ color: '#94a3b8' }}>
                {info.counts.fields} {t('sapSrc.dd.fields')} · {info.counts.keys} {t('sapSrc.dd.keys')}
                {info.counts.includes ? ` · ${info.counts.includes} ${t('sapSrc.dd.includes')}` : ''}
                {typeof info.took_ms === 'number' ? ` · ${info.took_ms} ms` : ''}
              </span>
            </div>
            {/* Sumber teks disebut eksplisit: DD02T untuk tabel, DD25T untuk view.
                Keduanya tidak boleh tertukar — lihat catatan di sapSource.js. */}
            <div style={{ marginTop: 6, fontSize: 11, color: '#94a3b8' }}>
              {t('sapSrc.dd.textSrc').replace('{src}', a.text_src).replace('{lang}', a.text_lang || '—')}
            </div>
          </div>

          {!!(info.warnings || []).length && (
            <div style={{ padding: '0.6rem 0.85rem', borderRadius: 8, marginBottom: 12,
              background: '#fffbeb', border: '1px solid #fde68a', color: '#92400e', fontSize: 12 }}>
              <b>{t('sapSrc.warnTitle')}</b>
              <ul style={{ margin: '4px 0 0', paddingLeft: 18 }}>
                {info.warnings.map((w, i) => <li key={i}>{w}</li>)}
              </ul>
            </div>
          )}

          {fields.length > 0 && (
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10, alignItems: 'center', marginBottom: 10 }}>
              <input value={filter} onChange={e => setFilter(e.target.value.toUpperCase())}
                placeholder={t('sapSrc.dd.filterPlaceholder')} spellCheck={false}
                style={{ flex: '1 1 220px', minWidth: 180, padding: '0.45rem 0.7rem', borderRadius: 8,
                  border: '1px solid #cbd5e1', fontSize: 12.5, fontFamily: MONO }} />
              <span style={{ fontSize: 12, color: '#94a3b8' }}>
                {shown.filter(f => f.kind === 'field').length} / {info.counts.fields}
              </span>
            </div>
          )}

          {fields.length === 0 ? (
            <div style={{ padding: '1rem', borderRadius: 8, background: '#f8fafc',
              border: '1px solid #e2e8f0', color: '#64748b', fontSize: 13 }}>
              {t('sapSrc.dd.noFields')}
            </div>
          ) : (
            <div style={{ overflowX: 'auto', border: '1px solid #e2e8f0', borderRadius: 10 }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12.5 }}>
                <thead>
                  <tr style={{ background: '#f8fafc', textAlign: 'left' }}>
                    {['sapSrc.dd.col.key', 'sapSrc.dd.col.pos', 'sapSrc.dd.col.field', 'sapSrc.dd.col.de',
                      'sapSrc.dd.col.type', 'sapSrc.dd.col.chk', 'sapSrc.dd.col.text'].map(k => (
                      <th key={k} style={{ padding: '0.5rem 0.7rem', color: '#64748b', fontWeight: 600 }}>{t(k)}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {shown.map(f => f.kind !== 'field' ? (
                    // Baris include/append: SATU baris menyeberangi seluruh kolom,
                    // bukan field. Ditampilkan sebagai field biasa, layar akan
                    // memperlihatkan "field bernama .INCLUDE" yang tidak ada di tabel.
                    <tr key={f.position + f.fieldname} style={{ borderTop: '1px solid #f1f5f9', background: '#fffdf5' }}>
                      <td style={{ padding: '0.45rem 0.7rem', fontFamily: MONO, color: '#94a3b8' }}>›</td>
                      <td style={{ padding: '0.45rem 0.7rem', fontFamily: MONO, color: '#94a3b8' }}>{f.position}</td>
                      <td colSpan={5} style={{ padding: '0.45rem 0.7rem', color: '#92400e' }}>
                        <span style={{ padding: '1px 6px', borderRadius: 4, fontSize: 10.5, fontWeight: 700,
                          background: '#fef3c7', color: '#92400e', marginRight: 8 }}>
                          {t(f.kind === 'append' ? 'sapSrc.dd.append' : 'sapSrc.dd.include')}
                        </span>
                        {f.structure ? navBtn(f.structure, f.structure) : '—'}
                        <span style={{ color: '#cbd5e1', fontFamily: MONO, marginLeft: 8 }}>{f.fieldname}</span>
                      </td>
                    </tr>
                  ) : (
                    <tr key={f.position + f.fieldname} style={{ borderTop: '1px solid #f1f5f9' }}>
                      <td style={{ padding: '0.45rem 0.7rem' }}>
                        {f.key && (
                          <span style={{ padding: '1px 5px', borderRadius: 3, fontSize: 10, fontWeight: 700,
                            background: 'rgb(var(--brand-100))', color: 'rgb(var(--brand-700))' }}>KEY</span>
                        )}
                      </td>
                      <td style={{ padding: '0.45rem 0.7rem', fontFamily: MONO, color: '#94a3b8' }}>{f.position}</td>
                      <td style={{ padding: '0.45rem 0.7rem', fontFamily: MONO, fontWeight: 600, color: '#0f172a' }}>{f.fieldname}</td>
                      <td style={{ padding: '0.45rem 0.7rem', fontFamily: MONO, color: '#334155' }}>{f.rollname || '—'}</td>
                      <td style={{ padding: '0.45rem 0.7rem', fontFamily: MONO, color: '#334155' }}>
                        {f.datatype || '—'}{f.leng ? ` (${f.leng}${f.decimals ? ',' + f.decimals : ''})` : ''}
                      </td>
                      <td style={{ padding: '0.45rem 0.7rem', fontFamily: MONO, color: '#334155' }}>
                        {f.checktable ? (f.checktable_nav ? navBtn(f.checktable, f.checktable) : f.checktable) : '—'}
                      </td>
                      <td style={{ padding: '0.45rem 0.7rem', color: '#475569' }}>{f.text || '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {/* Sampel isi tabel. Hanya untuk objek yang punya daftar field —
              untuk VIEW daftar itu belum tersedia, jadi panelnya pun tidak
              ditampilkan daripada menjanjikan sesuatu yang tidak bisa dipenuhi. */}
          {canPeek && !a.is_view && fields.length > 0 && (
            <TablePeek api={api} connId={connId} table={a.tabname} t={t} />
          )}
        </>
      )}
    </>
  );
}

// ── Landing: isi tampilan awal (statistik, recent, cache, perubahan di SAP) ──
const OBJ_LABEL = { PROG: 'Programs', FUGR: 'Function Groups', CLAS: 'Classes', TABL: 'Tables',
  INTF: 'Interfaces', DTEL: 'Data Elements', DOMA: 'Domains', VIEW: 'Views', TTYP: 'Table Types',
  DEVC: 'Packages', MSAG: 'Message Classes', TRAN: 'Transactions', ENHO: 'Enhancements' };
const SUBC_LABEL = { '1': 'Report', 'I': 'Include', 'M': 'Module pool', 'F': 'Function group',
  'K': 'Class pool', 'J': 'Interface pool', 'S': 'Subroutine pool', 'T': 'Type pool' };
const fmtUdat = d => (d && d.length === 8) ? `${d.slice(6, 8)}.${d.slice(4, 6)}.${d.slice(0, 4)}` : (d || '');
const fmtTs = s => {
  if (!s) return '';
  const dt = new Date(String(s).replace(' ', 'T') + 'Z');
  return isNaN(dt) ? s : dt.toLocaleString('en-GB', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' });
};

function LandingCard({ title, sub, right, children }) {
  return (
    <div style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: 12, padding: '0.9rem 1rem',
      display: 'flex', flexDirection: 'column', minWidth: 0 }}>
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, marginBottom: 8 }}>
        <div style={{ fontWeight: 700, fontSize: 13.5, color: '#0f172a' }}>{title}</div>
        {sub && <div style={{ fontSize: 11.5, color: '#94a3b8' }}>{sub}</div>}
        <div style={{ marginLeft: 'auto' }}>{right}</div>
      </div>
      {children}
    </div>
  );
}

function LandingRow({ name, meta, onClick }) {
  return (
    <button onClick={onClick}
      style={{ display: 'flex', width: '100%', gap: 8, alignItems: 'center', padding: '0.4rem 0.5rem',
        border: 'none', background: 'transparent', borderRadius: 6, cursor: 'pointer', textAlign: 'left' }}
      onMouseEnter={e => { e.currentTarget.style.background = '#f1f5f9'; }}
      onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; }}>
      <span style={{ fontFamily: 'ui-monospace, Menlo, Consolas, monospace', fontSize: 12.5, color: '#0369a1',
        fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', flex: '1 1 auto' }}>{name}</span>
      <span style={{ fontSize: 11.5, color: '#94a3b8', whiteSpace: 'nowrap' }}>{meta}</span>
    </button>
  );
}

const landingMuted = { fontSize: 12.5, color: '#94a3b8', padding: '0.4rem 0.5rem' };

const LANDING_KIND = {
  prog: { changed: 'Z*/Y* programs · live from TRDIR' },
  fm:   { changed: 'Z*/Y* function modules · live from TRDIR + TFDIR' },
  dd:   { changed: 'Z*/Y* tables & structures · live from DD02L' },
  cls:  { changed: 'Z*/Y* classes · live from SEOCLASSDF' },
};

function SourceLanding({ api, connId, onOpen, kind = 'prog' }) {
  const [home, setHome] = useState(null);
  const [homeErr, setHomeErr] = useState(null);
  const [chg, setChg] = useState(null);
  const [chgErr, setChgErr] = useState(null);
  const [chgLoad, setChgLoad] = useState(false);
  const [days, setDays] = useState(30);

  useEffect(() => {
    let alive = true;
    setHome(null); setHomeErr(null);
    api(`/api/sap/source/home?connectionId=${encodeURIComponent(connId)}&kind=${kind}`)
      .then(d => alive && setHome(d)).catch(e => alive && setHomeErr(e.message));
    return () => { alive = false; };
  }, [api, connId, kind]);

  useEffect(() => {
    let alive = true;
    setChgLoad(true); setChgErr(null);
    api(`/api/sap/source/recent-changes?connectionId=${encodeURIComponent(connId)}&days=${days}&kind=${kind}`)
      .then(d => alive && setChg(d)).catch(e => alive && setChgErr(e.message))
      .finally(() => alive && setChgLoad(false));
    return () => { alive = false; };
  }, [api, connId, days, kind]);

  const stats = (home && home.stats) || [];
  const totalObj = stats.reduce((a, s) => a + (s.n || 0), 0);
  const topStats = stats.slice(0, 5);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
      {/* Stat tiles */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: 10 }}>
        <StatTile label="Custom objects" value={home ? totalObj.toLocaleString('en-US') : '…'} accent />
        {topStats.map(s => (
          <StatTile key={s.object} label={OBJ_LABEL[s.object] || s.object} value={Number(s.n).toLocaleString('en-US')} />
        ))}
        <StatTile label="Cached programs" value={home ? Number(home.cached_total || 0).toLocaleString('en-US') : '…'} />
      </div>
      {homeErr && <div style={{ ...landingMuted, color: '#b91c1c' }}>{homeErr}</div>}

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: 12 }}>
        <LandingCard title="Recently changed in SAP" sub={LANDING_KIND[kind].changed}
          right={
            <select value={days} onChange={e => setDays(Number(e.target.value))}
              style={{ fontSize: 11.5, padding: '2px 4px', borderRadius: 6, border: '1px solid #cbd5e1', color: '#334155' }}>
              <option value={7}>7 days</option><option value={30}>30 days</option>
              <option value={90}>90 days</option><option value={365}>1 year</option>
            </select>
          }>
          {chgLoad && <div style={landingMuted}>Reading from SAP…</div>}
          {!chgLoad && chgErr && <div style={{ ...landingMuted, color: '#b91c1c' }}>{chgErr}</div>}
          {!chgLoad && !chgErr && chg && chg.rows.length === 0 && <div style={landingMuted}>No changes in the last {days} days.</div>}
          {!chgLoad && !chgErr && chg && chg.rows.map(r => (
            <LandingRow key={r.name} name={r.name}
              meta={[r.type, r.user, fmtUdat(r.date)].filter(Boolean).join(' · ')}
              onClick={() => onOpen(r.name)} />
          ))}
          {!chgLoad && chg && chg.total > chg.rows.length &&
            <div style={{ ...landingMuted, fontSize: 11.5 }}>Showing {chg.rows.length} of {chg.total}</div>}
        </LandingCard>

        <LandingCard title="Recently viewed" sub="from Activity Log">
          {!home && !homeErr && <div style={landingMuted}>Loading…</div>}
          {home && home.recent.length === 0 && <div style={landingMuted}>Nothing viewed yet on this system.</div>}
          {home && home.recent.map(r => (
            <LandingRow key={r.program} name={r.program} meta={`${r.user} · ${fmtTs(r.at)}`} onClick={() => onOpen(r.program)} />
          ))}
        </LandingCard>

        {kind === 'prog' && <LandingCard title="Cached programs" sub="opens instantly">
          {!home && !homeErr && <div style={landingMuted}>Loading…</div>}
          {home && home.cached.length === 0 && <div style={landingMuted}>No cached source yet.</div>}
          {home && home.cached.map(r => (
            <LandingRow key={r.progname} name={r.progname}
              meta={`${Number(r.lines).toLocaleString('en-US')} lines${r.last_read ? ' · ' + fmtTs(r.last_read) : ''}`}
              onClick={() => onOpen(r.progname)} />
          ))}
          {home && home.cached_total > home.cached.length &&
            <div style={{ ...landingMuted, fontSize: 11.5 }}>Showing {home.cached.length} of {home.cached_total}</div>}
        </LandingCard>}
      </div>
    </div>
  );
}

function StatTile({ label, value, accent }) {
  return (
    <div style={{ background: accent ? 'rgb(var(--brand-50, 240 253 250))' : '#fff', border: '1px solid #e2e8f0',
      borderRadius: 12, padding: '0.7rem 0.9rem' }}>
      <div style={{ fontSize: 11.5, color: '#64748b', fontWeight: 600 }}>{label}</div>
      <div style={{ fontSize: 22, fontWeight: 800, color: '#0f172a', marginTop: 2 }}>{value}</div>
    </div>
  );
}

export default function SAPSourceViewer() {
  const { t } = useLang();
  const api   = useApi();
  const activePageKey = useActiveDashboard();
  const [searchParams] = useSearchParams();

  const [conns, setConns]   = useState([]);
  const [connId, setConnId] = useState('');
  const [prog, setProg]     = useState('');
  const [withInc, setWithInc] = useState(false);

  const [sugOpen, setSugOpen] = useState(false);

  const [res, setRes]       = useState(null);
  const [loading, setLoad]  = useState(false);
  const [err, setErr]       = useState(null);
  const [copied, setCopied] = useState(false);

  const [find, setFind] = useState('');

  // Mode: 'prog' (report/include) atau 'fm' (function module, mirip SE37).
  // Dijadikan satu halaman, bukan dua menu: keduanya berbagi profil koneksi,
  // permission, dan pembacaan source yang sama persis. Dua halaman berarti dua
  // pemilih koneksi yang harus tetap sinkron — dan suatu hari tidak sinkron.
  const [mode, setMode] = useState('prog');
  // Tab Enhancements (inventaris exit/BAdI) butuh permission sap_customizing.
  const { user: authUser } = useAuth();
  const canEnh = !!authUser && (authUser.role === 'admin' || (authUser.permissions || []).includes('sap_customizing'));

  // Pencarian lintas tipe: hasil dari tipe lain memindahkan tab sekaligus
  // membawa nama objeknya. Anak komponen di-mount ulang saat mode berubah,
  // jadi cukup dioper lewat prop initial.
  const [jump, setJump] = useState(null);
  const goTo = useCallback((m, name) => {
    setJump({ mode: m, name: String(name || '').toUpperCase(), seq: Date.now() });
    setMode(m);
  }, []);
  const jumpFor = m => (jump && jump.mode === m ? jump.name : '');

  const boxRef = useRef(null);

  const conn = useMemo(() => conns.find(c => String(c.id) === String(connId)) || null, [conns, connId]);
  const progOk = PROG_RE.test(prog.trim().toUpperCase());
  const canRead = !!connId && progOk && !loading && !!(conn && conn.source_configured);

  // ── Muat profil koneksi ────────────────────────────────────────────────────
  useEffect(() => {
    let alive = true;
    api('/api/sap/source/connections')
      .then(d => {
        if (!alive) return;
        const list = (d && d.rows) || [];
        setConns(list);
        // Pilih profil pertama yang path_readsource-nya terisi: tanpa itu
        // pembacaan pasti gagal, dan membuka halaman dengan profil yang pasti
        // gagal hanya memindahkan kebingungan ke tombolnya.
        //
        // setConnId FUNGSIONAL: daftar datang dari jaringan, jadi hampir selalu
        // tiba SETELAH efek deep link. Menimpanya akan membuang profil yang
        // diminta tautan.
        setConnId(prev => prev || String((list.find(c => c.source_configured) || list[0] || {}).id || ''));
      })
      .catch(e => alive && setErr(e.message));
    return () => { alive = false; };
  }, [api]);

  // ── Deep link (?conn=&program=&inc=1) ──────────────────────────────────────
  // Penjagaan activePageKey wajib: tanpa itu halaman ini yang masih hidup di
  // balik display:none akan ikut berganti isi setiap kali URL berubah.
  useEffect(() => {
    if (activePageKey !== 'sap_source') return;
    const c = searchParams.get('conn');
    const p = searchParams.get('program');
    const i = searchParams.get('inc');
    // `tab` diterima juga: pencarian di header menavigasi ke ?tab=<mode>,
    // dan tab yang tidak bisa dituju dari sana sama saja dengan tidak ada.
    const md = searchParams.get('mode') || searchParams.get('tab');
    if (c && /^\d+$/.test(c)) setConnId(String(c));
    if (p) setProg(String(p).toUpperCase());
    if (i === '1') setWithInc(true);
    // Hanya nilai yang dikenal diterima: `mode` asing akan membuat kedua panel
    // tidak dirender sama sekali — layar kosong tanpa pesan apa pun.
    if (md === 'fm' || md === 'prog' || md === 'dd' || md === 'cls' || md === 'wb' || md === 'enh') setMode(md);
  }, [activePageKey, searchParams]);

  // ── Pencarian objek: KATALOG, bukan SAP ────────────────────────────────────
  // Sumbernya sap_wb_object hasil sync Custom Workbench — daftar yang sama
  // dipakai SEMUA tab. Saran lama hanya berisi program yang PERNAH dibaca,
  // jadi objek yang belum pernah dibuka tidak pernah muncul di pencarian.
  const wbSug = useWbSearch(api, connId, prog);

  // ── Baca source (LIVE) ─────────────────────────────────────────────────────
  const doRead = useCallback(async (override, opts) => {
    const p = String(override != null ? override : prog).trim().toUpperCase();
    if (!connId || !PROG_RE.test(p)) return;
    setLoad(true); setErr(null); setSugOpen(false);
    try {
      const d = await api(`/api/sap/source/read?connectionId=${encodeURIComponent(connId)}` +
                          `&program=${encodeURIComponent(p)}&withIncludes=${withInc ? '1' : '0'}` +
        ((opts && opts.fresh) ? '&fresh=1' : ''));
      setRes(d);
      // Hasil lama TIDAK dibiarkan menempel saat permintaan berikutnya gagal —
      // lihat catatan di blok catch.
    } catch (e) {
      setErr(e.message);
      // res dikosongkan: membiarkan source program SEBELUMNYA terpampang di
      // bawah pesan error adalah cara termudah membuat orang membaca kode yang
      // salah dan menyimpulkan hal yang salah tentang program yang baru diminta.
      setRes(null);
    } finally { setLoad(false); }
  }, [api, connId, prog, withInc]);

  // Lompatan lintas tipe yang mendarat di tab ini: nama objeknya langsung
  // dibaca. Berhenti di kotak input yang sudah terisi tapi masih kosong di
  // bawahnya akan terasa seperti klik yang tidak jadi.
  const jumpedRef = useRef(0);
  useEffect(() => {
    if (mode !== 'prog' || !jump || jump.mode !== 'prog') return;
    if (jumpedRef.current === jump.seq) return;
    jumpedRef.current = jump.seq;
    setProg(jump.name);
    doRead(jump.name);
  }, [mode, jump, doRead]);

  // ── Turunan tampilan ───────────────────────────────────────────────────────
  const groups = (res && res.groups) || [];
  const hitCount = useMemo(() => {
    const n = find.trim();
    if (!n) return 0;
    const nee = n.toUpperCase();
    let total = 0;
    for (const g of groups) for (const l of g.lines) {
      let i = 0, hay = String(l.content || '').toUpperCase();
      for (;;) { const at = hay.indexOf(nee, i); if (at < 0) break; total++; i = at + nee.length; }
    }
    return total;
  }, [groups, find]);

  const plainText = useMemo(() => groups.map(g =>
    `* ==== ${g.progname} (${g.lines.length} ${t('sapSrc.linesUnit')}) ====\n` +
    g.lines.map(l => String(l.lineno).padStart(5, ' ') + '  ' + (l.content || '')).join('\n')
  ).join('\n\n'), [groups, t]);

  const doCopy = async () => {
    try {
      await navigator.clipboard.writeText(plainText);
      setCopied(true); setTimeout(() => setCopied(false), 1600);
    } catch (_) { /* clipboard ditolak browser — tidak fatal, tombol saja diam */ }
  };

  const doDownload = () => {
    // Unduhan dibuat dari teks yang SAMA dengan yang tampil (plainText), bukan
    // dari susunan lain: berkas yang berbeda dari layar adalah jebakan.
    const blob = new Blob([plainText], { type: 'text/plain;charset=utf-8' });
    const url  = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${(res && res.program) || 'abap'}.abap.txt`;
    document.body.appendChild(a); a.click(); a.remove();
    setTimeout(() => URL.revokeObjectURL(url), 0);
  };

  const tone = STATE_TONE[(res && res.state) || ''] || STATE_TONE.missing;

  return (
    <div style={{ padding: '1.25rem', maxWidth: 1500, margin: '0 auto' }}>

      {/* ── Header ── */}
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12, alignItems: 'center', marginBottom: 14, background: "linear-gradient(135deg, rgb(var(--brand-600)) 0%, rgb(var(--brand-400)) 100%)", borderRadius: 14, padding: '16px 22px', boxShadow: '0 2px 8px rgba(0,0,0,0.08)' }}>
        <div style={{ flex: '1 1 260px' }}>
          <h1 style={{ fontSize: 20, fontWeight: 800, color: '#fff', margin: 0, letterSpacing: '-0.4px' }}>
            {t('page.sapSource.title')}
          </h1>
          <div style={{ fontSize: 12, color: 'rgba(255,255,255,0.75)', marginTop: 3 }}>
            {t('page.sapSource.subtitle')}
          </div>
        </div>

        <select
          value={connId}
          onChange={e => { setConnId(e.target.value); setRes(null); setErr(null); }}
          style={{ padding: '0.5rem 0.75rem', borderRadius: 8, border: '1px solid #cbd5e1',
            fontSize: 13, background: '#fff', minWidth: 220 }}
        >
          {conns.length === 0 && <option value="">—</option>}
          {conns.map(c => (
            <option key={c.id} value={c.id}>
              {[c.landscape, c.environment, c.sid].filter(Boolean).join(' / ')} — {c.host} ({c.client})
              {c.source_configured ? '' : ' ⚠'}
            </option>
          ))}
        </select>
      </div>

      {/* ── Batas informasi. Sengaja permanen, bukan bisa ditutup: batas yang
             bisa disembunyikan akan disembunyikan, lalu dilupakan. ── */}
      <div style={{ padding: '0.6rem 0.85rem', borderRadius: 8, marginBottom: 14,
        background: 'rgb(var(--brand-50))', border: '1px solid #bfdbfe', color: 'rgb(var(--brand-700))', fontSize: 12.5 }}>
        {mode === 'enh'
          ? 'Classic enhancements (CMOD/SMOD exits, screen exits, table appends) and BAdI implementations, from the last metadata snapshot. Enhancement spots are not scanned — absence here does not prove absence in SAP. Exit/BAdI source shown here is a copy of the last read, not a live view.'
          : t('sapSrc.scopeNote')}
      </div>

      {/* Profil tanpa path_readsource: dinyatakan di sini, sekali, dan tombol
          bacanya mati. Lebih jujur daripada tombol hidup yang pasti gagal. */}
      {mode !== 'enh' && conn && !conn.source_configured && (
        <div style={{ padding: '0.6rem 0.85rem', borderRadius: 8, marginBottom: 14,
          background: '#fffbeb', border: '1px solid #fde68a', color: '#92400e', fontSize: 12.5 }}>
          {t('sapSrc.noPath')}
        </div>
      )}

      {/* ── Pemilih mode ── */}
      <div style={{ display: 'inline-flex', gap: 4, padding: 3, borderRadius: 10,
        background: '#f1f5f9', marginBottom: 14 }}>
        {[['prog', 'sapSrc.mode.prog'], ['fm', 'sapSrc.mode.fm'], ['dd', 'sapSrc.mode.dd'], ['cls', 'sapSrc.mode.cls'], ['wb', 'sapSrc.mode.wb'], ...(canEnh ? [['enh', '']] : [])].map(([m, tk]) => (
          <button key={m} onClick={() => setMode(m)}
            style={{ padding: '0.4rem 0.9rem', borderRadius: 8, fontSize: 12.5,
              fontWeight: mode === m ? 700 : 500, cursor: 'pointer', border: 'none',
              background: mode === m ? '#fff' : 'transparent',
              color: mode === m ? '#0f172a' : '#64748b',
              boxShadow: mode === m ? '0 1px 3px rgba(15,23,42,0.10)' : 'none' }}>
            {m === 'wb' ? 'Custom Workbench' : m === 'enh' ? 'Enhancements' : t(tk)}
          </button>
        ))}
      </div>

      {mode === 'fm' && <FMViewer api={api} connId={connId} conn={conn} t={t}
        initial={jumpFor('fm')} onJump={goTo} />}
      {mode === 'dd' && <DDViewer api={api} connId={connId} t={t}
        initial={jumpFor('dd')} onJump={goTo} />}
      {mode === 'cls' && <ClassViewer api={api} connId={connId} conn={conn} t={t}
        initial={jumpFor('cls')} onJump={goTo} />}
      {mode === 'wb' && <SAPWorkbench connId={connId} initialQ={jumpFor('wb')} />}
      {mode === 'enh' && canEnh && <SAPCustomizing embedded connIdProp={connId} />}

      {mode === 'prog' && (
        <>
      {/* ── Pencarian program ── */}
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10, alignItems: 'flex-start', marginBottom: 14 }}>
        <div style={{ position: 'relative', flex: '1 1 320px', minWidth: 260 }} ref={boxRef}>
          <input
            value={prog}
            onChange={e => { setProg(e.target.value.toUpperCase()); setSugOpen(true); }}
            onFocus={() => setSugOpen(true)}
            onBlur={() => setTimeout(() => setSugOpen(false), 150)}
            onKeyDown={e => { if (e.key === 'Enter' && canRead) doRead(); if (e.key === 'Escape') setSugOpen(false); }}
            placeholder={t('sapSrc.progPlaceholder')}
            spellCheck={false}
            style={{ width: '100%', padding: '0.55rem 0.75rem', borderRadius: 8, fontSize: 13,
              fontFamily: MONO, letterSpacing: '0.02em',
              border: '1px solid ' + (prog && !progOk ? '#fca5a5' : '#cbd5e1') }}
          />
          {prog && !progOk && (
            <div style={{ fontSize: 11.5, color: '#b91c1c', marginTop: 4 }}>{t('sapSrc.progInvalid')}</div>
          )}

            {sugOpen && (
              <WbObjectSearch
                rows={wbSug.rows}
                total={wbSug.total}
                err={wbSug.err}
                mode="prog"
                onPick={n => { setProg(n); setSugOpen(false); doRead(n); }}
                onJump={(m, n) => { setSugOpen(false); goTo(m, n); }}
              />
            )}
        </div>

        <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12.5,
          color: '#334155', padding: '0.55rem 0' }}>
          <input type="checkbox" checked={withInc} onChange={e => setWithInc(e.target.checked)} />
          {t('sapSrc.withIncludes')}
        </label>

        <button
          onClick={() => doRead()}
          disabled={!canRead}
          style={{ padding: '0.55rem 1.1rem', borderRadius: 8, border: 'none', fontSize: 13,
            fontWeight: 600, cursor: canRead ? 'pointer' : 'not-allowed',
            background: canRead ? 'rgb(var(--brand-600))' : '#94a3b8', color: '#fff' }}
        >
          {loading ? t('sapSrc.reading') : t('sapSrc.read')}
        </button>
      </div>

      {err && (
        <div style={{ padding: '0.7rem 0.9rem', borderRadius: 8, marginBottom: 14,
          background: '#fef2f2', border: '1px solid #fecaca', color: '#991b1b', fontSize: 13 }}>
          {err}
        </div>
      )}

      {/* ── Hasil ── */}
      {res && (
        <>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10, alignItems: 'center',
            padding: '0.6rem 0.85rem', borderRadius: 8, marginBottom: 12,
            background: tone.bg, border: '1px solid ' + tone.border, color: tone.fg, fontSize: 12.5 }}>
            <span style={{ fontFamily: MONO, fontWeight: 700 }}>{res.program}</span>
            <span style={{ fontWeight: 600 }}>{t('sapSrc.state.' + res.state)}</span>
            <span style={{ opacity: 0.85 }}>
              {res.total_lines} {t('sapSrc.linesUnit')}
              {res.code ? ` · ${res.code} ${t('sapSrc.codeLines')}` : ''}
              {groups.length > 1 ? ` · ${groups.length} ${t('sapSrc.progUnit')}` : ''}
              {typeof res.took_ms === 'number' ? ` · ${res.took_ms} ms` : ''}
            </span>
            {res.note && <span style={{ opacity: 0.85 }}>· {res.note}</span>}
              {/* Sumber jawaban DINYATAKAN. Kode dari cache yang tampak seperti
                  kode terbaru adalah kesalahan yang paling mahal di halaman ini,
                  jadi tanggal tariknya ikut tampil bersama tombol baca ulang. */}
              {res.source === 'cache' && (
                <>
                  <span style={{ padding: '2px 8px', borderRadius: 999, background: '#e0f2fe',
                    border: '1px solid #bae6fd', color: '#075985', fontWeight: 600 }}>
                    cached{res.cached_at ? ' · ' + String(res.cached_at).slice(0, 16).replace('T', ' ') : ''}
                  </span>
                  <button onClick={() => doRead(res.program, { fresh: true })}
                    style={{ padding: '2px 10px', borderRadius: 6, border: '1px solid #94a3b8',
                      background: '#fff', color: '#0f172a', cursor: 'pointer', fontSize: 12 }}>
                    Read fresh from SAP
                  </button>
                </>
              )}
          </div>

          {/* LINE_TRUNC: dipotong oleh SAP di 255 karakter. Harus dinyatakan —
              kode yang terpotong diam-diam adalah kode yang salah dibaca. */}
          {!!res.trunc && (
            <div style={{ padding: '0.6rem 0.85rem', borderRadius: 8, marginBottom: 12,
              background: '#fffbeb', border: '1px solid #fde68a', color: '#92400e', fontSize: 12.5 }}>
              {t('sapSrc.truncWarn').replace('{n}', String(res.trunc))}
            </div>
          )}

          {groups.length > 0 && (
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10, alignItems: 'center', marginBottom: 10 }}>
              <input
                value={find}
                onChange={e => setFind(e.target.value)}
                placeholder={t('sapSrc.findPlaceholder')}
                spellCheck={false}
                style={{ flex: '1 1 220px', minWidth: 180, padding: '0.45rem 0.7rem', borderRadius: 8,
                  border: '1px solid #cbd5e1', fontSize: 12.5, fontFamily: MONO }}
              />
              <span style={{ fontSize: 12, color: find.trim() ? (hitCount ? '#0f172a' : '#b91c1c') : '#94a3b8' }}>
                {find.trim()
                  ? t('sapSrc.hits').replace('{n}', String(hitCount))
                  : t('sapSrc.findNote')}
              </span>
              <button onClick={doCopy}
                style={{ padding: '0.45rem 0.85rem', borderRadius: 8, border: '1px solid #cbd5e1',
                  background: '#fff', color: '#334155', fontSize: 12.5, fontWeight: 600, cursor: 'pointer' }}>
                {copied ? t('sapSrc.copied') : t('sapSrc.copy')}
              </button>
              <button onClick={doDownload}
                style={{ padding: '0.45rem 0.85rem', borderRadius: 8, border: '1px solid #cbd5e1',
                  background: '#fff', color: '#334155', fontSize: 12.5, fontWeight: 600, cursor: 'pointer' }}>
                {t('sapSrc.download')}
              </button>
            </div>
          )}

          {groups.length === 0 && (
            <div style={{ padding: '1rem', borderRadius: 8, background: '#f8fafc',
              border: '1px solid #e2e8f0', color: '#64748b', fontSize: 13 }}>
              {t('sapSrc.noLines')}
            </div>
          )}

          <SourceGroups groups={groups} first={res.program} find={find} t={t} />
        </>
      )}

      {!res && !err && !loading && (
        <>
          <div style={{ padding: '0.75rem 1rem', borderRadius: 10, background: '#f8fafc',
            border: '1px dashed #cbd5e1', color: '#64748b', fontSize: 12.5, marginBottom: 12 }}>
            {t('sapSrc.emptyState')}
          </div>
          {connId && (
            <SourceLanding api={api} connId={connId}
              onOpen={p => { setProg(p); doRead(p); }} />
          )}
        </>
      )}
        </>
      )}
    </div>
  );
}
